/**
 * AdminEditorialGuides — /admin/guides   (Phase 2)
 * ================================================
 * Adds:
 *  • Status-aware tabs (Draft · In review · Published) with live counts
 *  • Review actions per row (Submit, Approve, Reject) + status badge
 *  • A bulk-generate modal: pick a city + 11 templates, queue all at once
 *  • The original New-Guide form (manual single guide) is preserved
 *
 * Backend contract:
 *   GET    /api/editorial-guides?status=draft|in_review|published
 *   GET    /api/editorial-guides/_templates
 *   POST   /api/editorial-guides
 *   POST   /api/editorial-guides/bulk-generate
 *   POST   /api/editorial-guides/{id}/submit-for-review
 *   POST   /api/editorial-guides/{id}/approve
 *   POST   /api/editorial-guides/{id}/reject
 *   POST   /api/editorial-guides/{id}/regenerate-ai
 *   POST   /api/editorial-guides/{id}/publish     (legacy quick-toggle)
 *   DELETE /api/editorial-guides/{id}
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import RichTextEditor from '../components/RichTextEditor';
import {
  Plus, Loader2, RefreshCcw, ExternalLink, Eye, Trash2, Sparkles,
  ChevronRight, Layers, ClipboardCheck, X, FileCheck2, FileX2,
  CheckCheck, Send, Lightbulb, Grid3X3, AlertTriangle, Bot, BrainCircuit,
  Pencil, Pin, PinOff, Ban, Image as ImageIcon, Database, ShieldCheck,
  MapPin, Save, Wand2, Star, User as UserIcon, Ticket, ChevronUp, ChevronDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const titleize = (s = '') => String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const STATUS_TABS = [
  { id: 'all',       label: 'All',       color: 'bg-fuchsia-500' },
  { id: 'draft',     label: 'Drafts',    color: 'bg-amber-500'   },
  { id: 'in_review', label: 'In review', color: 'bg-indigo-500'  },
  { id: 'published', label: 'Published', color: 'bg-emerald-500' },
];

const STATUS_META = {
  draft:     { label: 'DRAFT',     cls: 'bg-amber-500/15 text-amber-300 border border-amber-500/30' },
  in_review: { label: 'IN REVIEW', cls: 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30' },
  published: { label: 'PUBLISHED', cls: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' },
};

const authHeaders = () => {
  const t = localStorage.getItem('eventism_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AdminEditorialGuides() {
  const { user } = useAuth();
  const [guides, setGuides] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, in_review: 0, published: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showCitySeed, setShowCitySeed] = useState(false);

  const fetchGuides = async (statusFilter = tab) => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const r = await axios.get(`${API}/editorial-guides`, { headers: authHeaders(), params });
      setGuides(r.data?.guides || []);
      setCounts(r.data?.counts || { draft: 0, in_review: 0, published: 0 });
    } catch (e) {
      toast.error('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGuides(tab); }, [tab]);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="border-border max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-muted-foreground">Admin access required.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12" data-testid="admin-editorial-guides">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-['Space_Grotesk']">Editorial Guides</h1>
            <p className="text-sm text-muted-foreground">
              {counts.draft} draft{counts.draft === 1 ? '' : 's'} ·
              {' '}{counts.in_review} in review ·
              {' '}{counts.published} published
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={async () => {
                if (!window.confirm('Run venue + guide seeds against this environment? Safe to re-run — all upserts are idempotent.')) return;
                setSeeding(true);
                try {
                  const r = await axios.post(`${API}/admin/seed-editorial`, {}, { headers: authHeaders() });
                  const d = r.data?.delta || {};
                  toast.success(`Seeded · +${d.venues || 0} venues, +${d.guides || 0} guides`);
                  // Auto-clear stale freshness flags after seeding.
                  await axios.post(`${API}/admin/refresh-guide-recipes`, {}, { headers: authHeaders() })
                    .then((rr) => {
                      if (rr.data?.summary) toast.message(rr.data.summary);
                    })
                    .catch(() => {});
                  fetchGuides(tab);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Seed failed');
                } finally { setSeeding(false); }
              }}
              variant="outline"
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="seed-venues-btn"
            >
              {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
              Seed venues
            </Button>
            <Button
              onClick={() => setShowCitySeed(true)}
              variant="outline"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              data-testid="seed-city-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Seed a city
            </Button>
            <Button
              onClick={async () => {
                if (refreshing) return;
                setRefreshing(true);
                try {
                  const r = await axios.post(`${API}/editorial-guides/refresh/all`, {}, { headers: authHeaders() });
                  toast.success(`Refreshed ${r.data?.refreshed || 0} guides${r.data?.flagged_count ? ` · ${r.data.flagged_count} flagged` : ''}`);
                  fetchGuides(tab);
                } catch {
                  toast.error('Refresh failed');
                } finally { setRefreshing(false); }
              }}
              variant="outline"
              data-testid="refresh-all-btn"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
              Refresh all
            </Button>
            <Button
              onClick={() => window.location.assign('/admin/editorial-bot')}
              variant="outline"
              className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              data-testid="open-editorial-bot"
              title="EventismEditorialBot — city intel, content strategy, guest author recruiting"
            >
              <BrainCircuit className="h-4 w-4 mr-2" /> EditorialBot
            </Button>
            <Button
              onClick={() => setShowSuggestions((s) => !s)}
              variant="outline"
              data-testid="suggestions-btn"
            >
              <Lightbulb className="h-4 w-4 mr-2" /> Suggestions
            </Button>
            <Button
              onClick={() => setShowMatrix(true)}
              variant="outline"
              data-testid="matrix-generate-btn"
            >
              <Grid3X3 className="h-4 w-4 mr-2" /> Matrix
            </Button>
            <Link to="/admin/editor-profile">
              <Button
                variant="outline"
                data-testid="editor-profile-link"
                title="Edit your author profile — shows as a hover card on every guide"
              >
                <UserIcon className="h-4 w-4 mr-2" /> Editor profile
              </Button>
            </Link>
            <Button
              onClick={() => setShowBulk(true)}
              className="bg-foreground text-background hover:bg-foreground/85"
              data-testid="bulk-generate-btn"
            >
              <Layers className="h-4 w-4 mr-2" /> Bulk generate
            </Button>
            <Button
              onClick={() => setShowCreate((s) => !s)}
              className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
              data-testid="new-guide-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> New guide
            </Button>
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="status-tab-row">
          {STATUS_TABS.map((t) => {
            const active = tab === t.id;
            const n = t.id === 'all'
              ? (counts.draft + counts.in_review + counts.published)
              : counts[t.id];
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                aria-selected={active}
                data-testid={`status-tab-${t.id}`}
                className={`h-9 px-4 rounded-full text-sm font-semibold transition-all border ${
                  active
                    ? 'bg-foreground text-background border-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-fuchsia-400/50'
                }`}
              >
                {t.label}
                <span className={`ml-2 inline-flex items-center justify-center text-[10px] font-bold px-1.5 rounded-full ${
                  active ? 'bg-background/15 text-background' : 'bg-muted text-muted-foreground'
                }`}>
                  {n ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {showCreate && (
          <NewGuideForm
            onCreated={() => { setShowCreate(false); fetchGuides(tab); }}
            onClose={() => setShowCreate(false)}
          />
        )}

        {showBulk && (
          <BulkGenerateModal
            onClose={() => setShowBulk(false)}
            onDone={() => { setShowBulk(false); setTab('draft'); fetchGuides('draft'); }}
          />
        )}

        {showMatrix && (
          <MatrixGenerateModal
            onClose={() => setShowMatrix(false)}
            onDone={() => { setShowMatrix(false); setTab('draft'); fetchGuides('draft'); }}
          />
        )}

        {showSuggestions && (
          <SuggestionsPanel onClose={() => setShowSuggestions(false)} />
        )}

        {showCitySeed && (
          <CitySeedModal
            onClose={() => setShowCitySeed(false)}
            onDone={() => { setShowCitySeed(false); fetchGuides(tab); }}
          />
        )}

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-fuchsia-300" />
          </div>
        ) : guides.length === 0 ? (
          <Card className="border-dashed border-2 border-border" data-testid="empty-state">
            <CardContent className="p-12 text-center space-y-3">
              <Sparkles className="h-8 w-8 mx-auto text-fuchsia-300" />
              <p className="text-base font-semibold">
                Nothing in <span className="text-fuchsia-300">{titleize(tab.replace('_', ' '))}</span>
              </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Click <strong>Bulk generate</strong> to queue 10 drafts in one shot, or
                <strong> New guide</strong> to author one by hand.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {guides.map((g) => (
              <GuideRow
                key={g.guide_id}
                guide={g}
                onChange={() => fetchGuides(tab)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Bulk generate modal — pick city + templates + categories, fire one request.
// ===========================================================================
function BulkGenerateModal({ onClose, onDone }) {
  const [city, setCity] = useState('denver');
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState({});      // { template_id: { checked, category_label } }
  const [submitting, setSubmitting] = useState(false);
  const [aiOn, setAiOn] = useState(true);
  const [fillPlaces, setFillPlaces] = useState(true);
  const [minVenues, setMinVenues] = useState(12);
  const [loadingTpls, setLoadingTpls] = useState(true);

  useEffect(() => {
    axios.get(`${API}/editorial-guides/_templates`, { headers: authHeaders() })
      .then((r) => {
        setTemplates(r.data?.templates || []);
        // Default-pick all template that don't require a category
        const init = {};
        (r.data?.templates || []).forEach((t) => {
          if (!t.needs_category) init[t.id] = { checked: true, category_label: '' };
          else                   init[t.id] = { checked: false, category_label: '' };
        });
        setSelected(init);
      })
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoadingTpls(false));
  }, []);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: { ...s[id], checked: !s[id]?.checked } }));
  const setCat = (id, v) => setSelected((s) => ({ ...s, [id]: { ...s[id], category_label: v } }));

  const submit = async () => {
    const items = templates
      .filter((t) => selected[t.id]?.checked)
      .map((t) => ({
        template_id: t.id,
        category_label: selected[t.id]?.category_label?.trim() || undefined,
      }));
    if (!items.length) return toast.error('Pick at least one template');
    // Block submit if a needs_category template has no category typed.
    const missingCat = items.find((i) => {
      const tpl = templates.find((t) => t.id === i.template_id);
      return tpl?.needs_category && !i.category_label;
    });
    if (missingCat) {
      const tpl = templates.find((t) => t.id === missingCat.template_id);
      return toast.error(`${tpl?.label || missingCat.template_id} needs a category label (e.g. "Wine Spots")`);
    }

    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/editorial-guides/bulk-generate`,
        {
          city: city.toLowerCase().trim(),
          items,
          auto_generate_ai: aiOn,
          fill_from_places: fillPlaces,
          min_venues: Number(minVenues) || 12,
        },
        { headers: authHeaders() },
      );
      const created = r.data?.created?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      const filled = r.data?.places_filled || 0;
      const filledMsg = filled > 0 ? ` · +${filled} venue${filled === 1 ? '' : 's'} from Places` : '';
      toast.success(`Created ${created} draft${created === 1 ? '' : 's'}${skipped ? ` · ${skipped} skipped (already exist)` : ''}${filledMsg}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk generate failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-20 overflow-y-auto" data-testid="bulk-generate-modal">
      <Card className="max-w-3xl w-full border-fuchsia-500/30 bg-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-xl font-bold font-['Space_Grotesk']">Bulk generate guides</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Pick a city + the templates you want. Each one becomes a <strong>draft</strong> in the review queue.
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="bulk-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid sm:grid-cols-[1fr_auto] gap-3 mb-4">
            <div className="space-y-1.5">
              <Label>City slug</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="denver" data-testid="bulk-city" />
            </div>
            <label className="flex items-end gap-2 text-sm pb-2">
              <input
                type="checkbox"
                checked={aiOn}
                onChange={(e) => setAiOn(e.target.checked)}
                data-testid="bulk-ai-toggle"
              />
              AI intro + FAQs
            </label>
          </div>

          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2 mb-5" data-testid="bulk-places-fill-block">
            <label className="flex items-center gap-2 text-sm font-semibold text-amber-200">
              <input
                type="checkbox"
                checked={fillPlaces}
                onChange={(e) => setFillPlaces(e.target.checked)}
                data-testid="bulk-places-toggle"
              />
              Auto-fill from Google Places when the DB is short
            </label>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              For each template, if fewer than the target below match locally, pull the rest from Google Places (photos + address + AI blurb baked in). Grows your venue directory with every batch.
            </p>
            {fillPlaces && (
              <div className="flex items-center gap-2 text-xs">
                <Label className="text-muted-foreground shrink-0">Target minimum venues</Label>
                <Input
                  type="number" min={1} max={30}
                  value={minVenues}
                  onChange={(e) => setMinVenues(e.target.value)}
                  className="h-8 w-20"
                  data-testid="bulk-min-venues"
                />
                <span className="text-muted-foreground">per guide</span>
              </div>
            )}
          </div>

          {loadingTpls ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <div className="space-y-2 mb-5 max-h-[400px] overflow-y-auto pr-1">
              {templates.map((t) => {
                const row = selected[t.id] || {};
                return (
                  <div
                    key={t.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                      row.checked ? 'border-fuchsia-500/40 bg-fuchsia-500/5' : 'border-border'
                    }`}
                    data-testid={`bulk-row-${t.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!row.checked}
                      onChange={() => toggle(t.id)}
                      className="mt-1"
                      data-testid={`bulk-check-${t.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{t.label}</p>
                      <p className="text-[11px] text-muted-foreground">{t.id}</p>
                      {t.needs_category && row.checked && (
                        <Input
                          placeholder='e.g. "Wine Spots", "Halloween"'
                          value={row.category_label || ''}
                          onChange={(e) => setCat(t.id, e.target.value)}
                          className="mt-2 h-8 text-xs"
                          data-testid={`bulk-cat-${t.id}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              {Object.values(selected).filter((s) => s?.checked).length} template{Object.values(selected).filter((s) => s?.checked).length === 1 ? '' : 's'} selected
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
                data-testid="bulk-submit"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
                  : <>Generate drafts</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// New guide form (single, hand-crafted)
// ===========================================================================
function NewGuideForm({ onCreated, onClose }) {
  const [form, setForm] = useState({
    title: 'Best Wine Spots in Denver',
    city: 'denver',
    category_label: 'Wine Spots',
    seo_title: '',
    template: 'best_category_in_city',
    tags_csv: 'wine, natural wine, wine bar, winery',
    venue_types_csv: 'wine_bar, winery, restaurant, bar',
    raw_copy: '',
    cover_image: '',
    auto_generate_ai: true,
    fill_from_places: true,
    min_venues: 12,
    // "venues" = classic guide, populated by recipe.
    // "events" = admin-curated event cards; tickets surface above each card.
    subject_type: 'venues',
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.title || form.title.length < 4) return toast.error('Title is required');
    if (!form.city) return toast.error('City is required');
    if (!form.category_label) return toast.error('Category label is required');
    setSubmitting(true);
    try {
      const csv = (s) => (s || '').split(',').map((t) => t.trim()).filter(Boolean);
      const recipe = {
        tags_any: csv(form.tags_csv),
        venue_types: csv(form.venue_types_csv),
        include_event_kinds: ['event', 'experience', 'reservation'],
      };
      const resp = await axios.post(
        `${API}/editorial-guides`,
        {
          title: form.title.trim(),
          seo_title: form.seo_title.trim() || undefined,
          city: form.city.trim().toLowerCase(),
          template: form.template,
          category_label: form.category_label.trim(),
          recipe,
          raw_copy: form.raw_copy.trim() || undefined,
          cover_image: form.cover_image || undefined,
          auto_generate_ai: form.auto_generate_ai && form.subject_type !== 'events',
          fill_from_places: form.fill_from_places && form.subject_type !== 'events',
          min_venues: Number(form.min_venues) || 12,
          subject_type: form.subject_type || 'venues',
          // For event guides we skip the AI addendum pass — cards are curated.
          venue_addendum_fields: form.subject_type === 'events' ? [] : undefined,
        },
        { headers: authHeaders() },
      );
      const filled = resp.data?.places_filled || 0;
      const asm = resp.data?.assembly;
      if (asm && !asm.error) {
        const unmatched = asm.unmatched_venue_names || [];
        toast.success(
          `Guide assembled from your copy — ${asm.matched_venues}/${asm.sections} venue${asm.sections === 1 ? '' : 's'} matched & pinned.${
            unmatched.length ? ` Not in our DB yet: ${unmatched.join(', ')}.` : ''
          }`,
          { duration: 8000 },
        );
      } else if (asm?.error) {
        toast.warning('Draft created, but the AI assembler was unavailable — copy was not applied. Edit the guide manually or retry.');
      } else if (filled > 0) {
        toast.success(`Draft created — auto-imported ${filled} venue${filled === 1 ? '' : 's'} from Google Places.`);
      } else {
        toast.success('Draft created — review and publish from the queue.');
      }
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/5 via-transparent to-transparent mb-6" data-testid="new-guide-form">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <h2 className="text-base font-bold">Author a new guide</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 space-y-2" data-testid="form-subject-type-block">
            <Label className="text-xs">Guide type</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, subject_type: 'venues' }))}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  form.subject_type === 'venues'
                    ? 'border-fuchsia-500 bg-fuchsia-500/10'
                    : 'border-border/50 hover:border-fuchsia-500/40'
                }`}
                data-testid="form-subject-venues"
              >
                <p className="text-sm font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-fuchsia-300" /> Venue guide
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Recipe-driven list of venues. Auto-fills from your DB (+ Google Places).
                </p>
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, subject_type: 'events' }))}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  form.subject_type === 'events'
                    ? 'border-indigo-500 bg-indigo-500/10'
                    : 'border-border/50 hover:border-indigo-500/40'
                }`}
                data-testid="form-subject-events"
              >
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-indigo-300" /> Event guide
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Hand-curated cards. Live tickets auto-surface above each card image.
                </p>
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Title (becomes the H1)</Label>
            <Input value={form.title} onChange={onChange('title')} data-testid="form-title" />
          </div>
          <div className="space-y-1.5">
            <Label>SEO title (optional — the &lt;title&gt; tag; falls back to the H1)</Label>
            <Input value={form.seo_title} onChange={onChange('seo_title')} placeholder="e.g. 12 Best Karaoke Bars in Denver (2026 Guide)" data-testid="form-seo-title" />
          </div>
          <div className="space-y-1.5">
            <Label>City slug</Label>
            <Input value={form.city} onChange={onChange('city')} placeholder="denver" data-testid="form-city" />
          </div>

          <div className="space-y-1.5">
            <Label>Category label</Label>
            <Input value={form.category_label} onChange={onChange('category_label')} placeholder="Wine Spots" data-testid="form-category-label" />
          </div>
          <div className="space-y-1.5">
            <Label>Cover image (optional)</Label>
            <CoverImageUploader
              value={form.cover_image}
              onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))}
            />
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label>Tags / vibe (comma separated)</Label>
            <Input value={form.tags_csv} onChange={onChange('tags_csv')} placeholder="wine, natural wine, wine bar" data-testid="form-tags" />
            {form.subject_type === 'events' && (
              <p className="text-[10px] text-muted-foreground">
                Tags are only used as a hint for the intro. You&apos;ll define per-card recipes after saving.
              </p>
            )}
          </div>

          {form.subject_type === 'venues' && (
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Venue types (comma separated)</Label>
              <Input value={form.venue_types_csv} onChange={onChange('venue_types_csv')} placeholder="wine_bar, winery, restaurant, bar" data-testid="form-venue-types" />
            </div>
          )}

          <div className="sm:col-span-2 space-y-1.5 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3" data-testid="form-raw-copy-block">
            <Label className="flex items-center gap-1.5 text-fuchsia-200">
              <Wand2 className="h-3.5 w-3.5" /> Drop your guide copy (optional)
            </Label>
            <Textarea
              value={form.raw_copy}
              onChange={onChange('raw_copy')}
              rows={8}
              placeholder={"Paste your draft here — intro, venue names, per-venue write-ups, any format.\n\nEventism AI assembles the guide from it: matches venues to our directory, pins them, and fills in anything you didn't add (photos, vibe tags, meta description, FAQs) so the guide ships at Eventism's best practices."}
              className="text-sm"
              data-testid="form-raw-copy"
            />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              The AI keeps your voice — it only writes the pieces you leave out. Venues it can&apos;t find in our directory are reported so you can add them.
            </p>
          </div>

          <label className="sm:col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.auto_generate_ai}
              onChange={(e) => setForm((f) => ({ ...f, auto_generate_ai: e.target.checked }))}
              data-testid="form-ai-toggle"
              disabled={form.subject_type === 'events'}
            />
            Auto-generate intro + 5 FAQs with Claude
            {form.subject_type === 'events' && (
              <span className="text-[10px] text-muted-foreground ml-2">
                (disabled for event guides — cards are curated manually)
              </span>
            )}
          </label>

          {form.subject_type === 'venues' && (
            <div className="sm:col-span-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="form-places-fill-block">
            <label className="flex items-center gap-2 text-sm font-semibold text-amber-200">
              <input
                type="checkbox"
                checked={form.fill_from_places}
                onChange={(e) => setForm((f) => ({ ...f, fill_from_places: e.target.checked }))}
                data-testid="form-places-toggle"
              />
              Auto-fill from Google Places if the DB is short
            </label>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              When your recipe matches fewer venues than the target below, we&apos;ll pull the gap from Google Places and auto-import them (photos + AI blurb + address). Every guide reads full and your venue directory grows on autopilot.
            </p>
            {form.fill_from_places && (
              <div className="flex items-center gap-2 text-xs">
                <Label className="text-muted-foreground shrink-0">Target minimum venues</Label>
                <Input
                  type="number" min={1} max={30}
                  value={form.min_venues}
                  onChange={onChange('min_venues')}
                  className="h-8 w-20"
                  data-testid="form-min-venues"
                />
                <span className="text-muted-foreground">venues per guide</span>
              </div>
            )}
            </div>
          )}

          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
              data-testid="form-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <>Create draft</>}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Guide row — status pill + workflow actions
// ===========================================================================
function GuideRow({ guide, onChange }) {
  const [working, setWorking] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const status = guide.status || (guide.is_published ? 'published' : 'draft');
  const meta = STATUS_META[status];

  const callAction = async (action, label) => {
    setWorking(action);
    try {
      await axios.post(`${API}/editorial-guides/${guide.guide_id}/${action}`, {}, { headers: authHeaders() });
      toast.success(label);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally { setWorking(''); }
  };

  const regen = () => callAction('regenerate-ai', 'AI copy regenerated');
  const submit = () => callAction('submit-for-review', 'Submitted for review');
  const approve = () => callAction('approve', 'Approved & published');
  const reject = async () => {
    const comment = window.prompt('Reject reason (optional):') || '';
    setWorking('reject');
    try {
      await axios.post(`${API}/editorial-guides/${guide.guide_id}/reject`, { comment }, { headers: authHeaders() });
      toast.success('Sent back to draft');
      onChange();
    } catch {
      toast.error('Reject failed');
    } finally { setWorking(''); }
  };
  const [confirmDel, setConfirmDel] = useState(false);
  const del = async () => {
    // Two-step inline confirm — window.confirm is unreliable (blocked in
    // sandboxed iframes / some browsers), which made delete look dead.
    if (!confirmDel) {
      setConfirmDel(true);
      setTimeout(() => setConfirmDel(false), 4000);
      return;
    }
    setConfirmDel(false);
    setWorking('delete');
    try {
      await axios.delete(`${API}/editorial-guides/${guide.guide_id}`, { headers: authHeaders() });
      toast.success('Deleted');
      onChange();
    } catch {
      toast.error('Delete failed');
    } finally { setWorking(''); }
  };
  const fillPlaces = async () => {
    setWorking('fill-places');
    try {
      const r = await axios.post(
        `${API}/editorial-guides/${guide.guide_id}/fill-from-places`,
        { min_venues: 12 },
        { headers: authHeaders() },
      );
      const n = r.data?.imported || 0;
      if (n === 0) {
        toast.success(r.data?.message || 'No new venues needed.');
      } else {
        toast.success(`Imported ${n} new venue${n === 1 ? '' : 's'} from Google Places.`);
      }
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fill from Places failed');
    } finally { setWorking(''); }
  };

  const publicUrl = `/${guide.city}/guides/${guide.slug}`;

  return (
    <Card className="border-border hover:border-fuchsia-400/40 transition-colors" data-testid={`guide-row-${guide.guide_id}`}>
      <CardContent className="p-4 grid sm:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge className={`text-[10px] font-bold border-0 ${meta.cls}`} data-testid={`guide-status-${guide.guide_id}`}>
              {meta.label}
            </Badge>
            {(guide.subject_type || 'venues') === 'events' && (
              <Badge
                className="text-[10px] font-bold border-0 bg-indigo-500/25 text-indigo-200"
                data-testid={`guide-subject-events-${guide.guide_id}`}
              >
                <Ticket className="h-2.5 w-2.5 mr-1" /> EVENT GUIDE
              </Badge>
            )}
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-300">{titleize(guide.city)}</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">· {guide.category_label}</span>
          </div>
          <p className="font-bold text-base truncate" data-testid={`guide-title-${guide.guide_id}`}>{guide.title}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {publicUrl}
            {guide.last_verified_at && <> · verified {new Date(guide.last_verified_at).toLocaleDateString()}</>}
            {typeof guide.view_count === 'number' && <> · {guide.view_count} views</>}
          </p>
          {guide.review_comment && (
            <p className="text-[11px] text-amber-300 mt-1 italic" data-testid={`guide-comment-${guide.guide_id}`}>
              Reviewer: {guide.review_comment}
            </p>
          )}
          {guide.needs_review && (
            <p className="text-[11px] text-red-300 mt-1 flex items-center gap-1" data-testid={`guide-flag-${guide.guide_id}`}>
              <AlertTriangle className="h-3 w-3" /> Freshness sweep flagged this guide — recipe matched 0 venues.
            </p>
          )}
          {guide.ai_citations && (guide.ai_citations.count || 0) > 0 && (
            <span
              className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 text-fuchsia-300 text-[11px] font-semibold cursor-help"
              title={`Cited by: ${(guide.ai_citations.agents || []).join(', ')}`}
              data-testid={`guide-ai-cited-${guide.guide_id}`}
            >
              <Bot className="h-3 w-3" /> Cited {guide.ai_citations.count}× by AI
              {(guide.ai_citations.agents || []).length > 0 && (
                <span className="opacity-70">
                  · {(guide.ai_citations.agents || []).slice(0, 3).join(' · ')}
                  {(guide.ai_citations.agents || []).length > 3 ? ' +' : ''}
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Link to={publicUrl} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" data-testid={`guide-view-${guide.guide_id}`}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> {status === 'published' ? 'View' : 'Preview'}
            </Button>
          </Link>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditOpen(true)}
            data-testid={`guide-edit-${guide.guide_id}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
          </Button>
          <Button size="sm" variant="outline" onClick={regen} disabled={!!working} data-testid={`guide-regen-${guide.guide_id}`}>
            {working === 'regenerate-ai'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Re-gen AI</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={fillPlaces}
            disabled={!!working}
            className="text-amber-300 border-amber-500/30 hover:bg-amber-500/10"
            data-testid={`guide-fill-places-${guide.guide_id}`}
            title="Auto-import venues from Google Places to fill this guide"
          >
            {working === 'fill-places'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Fill from Places</>}
          </Button>

          {/* Status-aware workflow actions */}
          {status === 'draft' && (
            <Button
              size="sm"
              onClick={submit}
              disabled={!!working}
              className="bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid={`guide-submit-${guide.guide_id}`}
            >
              {working === 'submit-for-review' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Submit</>}
            </Button>
          )}
          {status === 'in_review' && (
            <>
              <Button
                size="sm"
                onClick={approve}
                disabled={!!working}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid={`guide-approve-${guide.guide_id}`}
              >
                {working === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCheck className="h-3.5 w-3.5 mr-1.5" /> Approve</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={reject}
                disabled={!!working}
                className="text-amber-300 border-amber-500/30 hover:bg-amber-500/10"
                data-testid={`guide-reject-${guide.guide_id}`}
              >
                {working === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileX2 className="h-3.5 w-3.5 mr-1.5" /> Reject</>}
              </Button>
            </>
          )}
          {status === 'published' && (
            <Button
              size="sm"
              onClick={() => callAction('publish', 'Unpublished')}
              disabled={!!working}
              variant="outline"
              data-testid={`guide-unpublish-${guide.guide_id}`}
            >
              {working === 'publish' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Eye className="h-3.5 w-3.5 mr-1.5" /> Unpublish</>}
            </Button>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={del}
            disabled={!!working}
            className={confirmDel
              ? 'text-white bg-red-500 hover:bg-red-400 border-red-500'
              : 'text-red-300 border-red-500/30 hover:bg-red-500/10'}
            data-testid={`guide-delete-${guide.guide_id}`}
          >
            {working === 'delete'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : confirmDel
                ? <>Confirm delete?</>
                : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
          <ChevronRight className="h-4 w-4 text-muted-foreground hidden sm:inline" />
        </div>
      </CardContent>
      {editOpen && (
        <ErrorBoundary onClose={() => setEditOpen(false)}>
          <EditGuideModal
            guide={guide}
            onClose={() => setEditOpen(false)}
            onSaved={() => { setEditOpen(false); onChange(); }}
          />
        </ErrorBoundary>
      )}
    </Card>
  );
}

// ===========================================================================
// UnderPhotoAddendumPanel — 5 toggles + per-venue AI-generated content
// that renders under each venue's photo gallery. Batch-generates the
// enabled fields for every venue in the guide, then lets the editor
// override any line inline.
// ===========================================================================
const ADDENDUM_FIELD_LABELS = {
  location: 'Location',
  best_for: 'Best for',
  views_of: 'Views of',
  why_recommend: 'Why we recommend',
  people_saying: 'People are saying',
};
const ADDENDUM_ORDER = ['location', 'best_for', 'views_of', 'why_recommend', 'people_saying'];

function UnderPhotoAddendumPanel({ guide, venues, onSaved }) {
  const [enabled, setEnabled] = useState(new Set(guide.venue_addendum_fields || []));
  const [addendum, setAddendum] = useState(guide.venue_addendum || {});
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  // Per-venue card-description overrides — saved onto the venue doc itself
  // (same place the public card reads from) alongside the addendum lines.
  const [descBuf, setDescBuf] = useState({});

  const toggle = (f) => {
    setEnabled((cur) => {
      const next = new Set(cur);
      if (next.has(f)) next.delete(f); else next.add(f);
      return next;
    });
  };

  const generate = async (force = false) => {
    const fields = Array.from(enabled);
    if (!fields.length) return toast.error('Toggle at least one line first');
    setGenerating(true);
    const t = toast.loading(force ? 'Regenerating for all venues…' : 'Generating for all venues…');
    try {
      const r = await axios.post(
        `${API}/editorial-guides/${guide.guide_id}/generate-addendum`,
        { fields, force },
        { headers: authHeaders() },
      );
      setAddendum(r.data?.venue_addendum || {});
      onSaved?.(r.data?.fields || fields, r.data?.venue_addendum || {});
      toast.success(`Filled ${r.data?.venue_count || 0} of ${r.data?.total_venues || 0} venue${r.data?.total_venues === 1 ? '' : 's'}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed', { id: t });
    } finally {
      setGenerating(false);
    }
  };

  const savePersist = async () => {
    // Save the toggle set (and any inline edits to `addendum`) without
    // re-running the LLM. Also persists edited card descriptions onto
    // their venue docs.
    setSaving(true);
    try {
      const changedDescs = Object.entries(descBuf).filter(([vid, val]) => {
        const orig = ((venues || []).find((v) => v.venue_id === vid)?.description) || '';
        return val !== orig;
      });
      for (const [vid, val] of changedDescs) {
        await axios.put(`${API}/admin/venues/${vid}/edit`, { description: val }, { headers: authHeaders() });
      }
      await axios.put(
        `${API}/editorial-guides/${guide.guide_id}`,
        {
          venue_addendum_fields: Array.from(enabled),
          venue_addendum: addendum,
        },
        { headers: authHeaders() },
      );
      onSaved?.(Array.from(enabled), addendum);
      toast.success(changedDescs.length
        ? `Saved — ${changedDescs.length} description${changedDescs.length === 1 ? '' : 's'} updated`
        : 'Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const rewriteAll = async (force = false) => {
    if (force && !window.confirm('Regenerate the description for EVERY venue in this guide (even ones already rewritten)?')) {
      return;
    }
    setRewriting(true);
    const t = toast.loading('Rewriting descriptions in Eventism voice…');
    try {
      const r = await axios.post(
        `${API}/editorial-guides/${guide.guide_id}/rewrite-descriptions`,
        { force },
        { headers: authHeaders(), timeout: 180000 },
      );
      const n = r.data?.rewritten || 0;
      const errs = r.data?.errors || 0;
      if (n === 0 && !errs) {
        toast.success(r.data?.message || 'Nothing to rewrite', { id: t });
      } else {
        toast.success(`Rewrote ${n} venue description${n === 1 ? '' : 's'}${errs ? ` · ${errs} error${errs === 1 ? '' : 's'}` : ''}`, { id: t });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rewrite failed', { id: t });
    } finally {
      setRewriting(false);
    }
  };

  const editLine = (venueId, field, value) => {
    setAddendum((cur) => ({
      ...cur,
      [venueId]: { ...(cur[venueId] || {}), [field]: value },
    }));
  };

  // Only show the venues that are actually IN this guide (resolved list),
  // not every venue_id that ever landed in the addendum dict — stale keys
  // from earlier recipe runs made this panel show other guides' venues.
  const guideVenues = (venues || []).filter((v) => v.venue_id);
  const nameById = Object.fromEntries(guideVenues.map((v) => [v.venue_id, v.name]));
  const venueIds = guideVenues.length
    ? guideVenues.map((v) => v.venue_id)
    : Object.keys(addendum || {});

  return (
    <div
      className="mt-5 pt-4 border-t border-border space-y-3"
      data-testid="edit-venues-addendum-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
            Under-photo info
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Toggle which lines appear under every venue&apos;s photo gallery. Click Generate to have Claude write one line per enabled field per venue — you can edit each below.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => generate(false)}
            disabled={generating || enabled.size === 0}
            data-testid="addendum-generate-missing"
            title="Only writes fields that don't already have content"
          >
            {generating
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Sparkles className="h-3.5 w-3.5 mr-1.5 text-fuchsia-300" /> Generate missing</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => generate(true)}
            disabled={generating || enabled.size === 0}
            className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
            data-testid="addendum-regenerate-all"
            title="Overwrite existing content for every venue"
          >
            {generating
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Regenerate all</>}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2" data-testid="addendum-toggles">
        {ADDENDUM_ORDER.map((f) => (
          <label
            key={f}
            className="flex items-center gap-2 text-sm p-2 rounded-md border border-border cursor-pointer hover:border-fuchsia-500/40"
            data-testid={`addendum-toggle-${f}`}
          >
            <input
              type="checkbox"
              checked={enabled.has(f)}
              onChange={() => toggle(f)}
            />
            <span className="font-semibold">{ADDENDUM_FIELD_LABELS[f]}:</span>
            <span className="text-muted-foreground text-[11px] truncate">
              {f === 'location' && 'Neighborhood + landmark'}
              {f === 'best_for' && 'Ideal use case'}
              {f === 'views_of' && 'What guests see / feel'}
              {f === 'why_recommend' && 'Editor rationale'}
              {f === 'people_saying' && 'Review-voice quote'}
            </span>
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setExpanded((s) => !s)}
          className="text-xs text-fuchsia-300 hover:text-fuchsia-200 font-semibold"
          data-testid="addendum-expand-toggle"
        >
          {expanded ? '▾ Hide' : '▸ Show'} per-venue edits ({venueIds.length})
        </button>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => rewriteAll(false)}
            disabled={rewriting}
            className="border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10"
            data-testid="rewrite-descriptions-missing"
            title="Rewrites each venue's description in Eventism voice — skips venues already rewritten"
          >
            {rewriting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Wand2 className="h-3.5 w-3.5 mr-1.5" /> Rewrite descriptions</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => rewriteAll(true)}
            disabled={rewriting}
            className="border-amber-500/30 text-amber-200 hover:bg-amber-500/10"
            data-testid="rewrite-descriptions-force"
            title="Overwrite EVERY description (including previously-rewritten ones)"
          >
            {rewriting
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Rewrite all</>}
          </Button>
          <Button
            size="sm"
            onClick={savePersist}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-400 text-white"
            data-testid="addendum-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save toggles + edits</>}
          </Button>
        </div>
      </div>

      {expanded && venueIds.length > 0 && (
        <div className="space-y-3 max-h-96 overflow-y-auto pr-1" data-testid="addendum-editor">
          {venueIds.map((vid) => {
            const venue = guideVenues.find((v) => v.venue_id === vid);
            return (
            <div key={vid} className="rounded-lg border border-border p-3 space-y-2 bg-background/40" data-testid={`addendum-row-${vid}`}>
              <p className="text-[11px] font-semibold truncate">
                {nameById[vid] || <span className="font-mono text-muted-foreground">{vid}</span>}
              </p>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Card description (shown under the photos)</Label>
                <Textarea
                  value={descBuf[vid] !== undefined ? descBuf[vid] : (venue?.description || '')}
                  onChange={(e) => setDescBuf((cur) => ({ ...cur, [vid]: e.target.value }))}
                  rows={3}
                  placeholder="Paste your editorial write-up for this venue…"
                  className="text-xs"
                  data-testid={`addendum-desc-${vid}`}
                />
              </div>
              {ADDENDUM_ORDER.map((f) => (
                <div key={f} className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">
                    {ADDENDUM_FIELD_LABELS[f]}
                    {!enabled.has(f) && <span className="text-amber-300/80"> · hidden — toggle on above to display</span>}
                  </Label>
                  <Input
                    value={(addendum[vid] || {})[f] || ''}
                    onChange={(e) => editLine(vid, f, e.target.value)}
                    placeholder={`Empty — click Generate to fill`}
                    className="h-8 text-xs"
                    data-testid={`addendum-input-${vid}-${f}`}
                  />
                </div>
              ))}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// VenuePhotoTicketPanel — per-venue Photos (saved on the venue doc) + ticket
// banner control (saved on the guide as venue_ticket_overrides). Gives the
// Venues tab the same media/ticket powers the Cards tab already has.
// ---------------------------------------------------------------------------
function VenuePhotoTicketPanel({ venue, override, onSave, onPhotosSaved }) {
  const [photosBuf, setPhotosBuf] = useState(() => (
    (venue.photos && venue.photos.length ? venue.photos : (venue.image_url ? [venue.image_url] : [])).join('\n')
  ));
  const [mode, setMode] = useState(override?.mode || 'auto');
  const [tagsBuf, setTagsBuf] = useState((override?.tags || []).join(', '));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const photos = photosBuf.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 8);
      if (venue.venue_id) {
        await axios.put(`${API}/admin/venues/${venue.venue_id}/edit`, {
          photos,
          cover_photo: photos[0] || null,
          image_url: photos[0] || venue.image_url || null,
        }, { headers: authHeaders() });
      }
      const tags = tagsBuf.split(',').map((s) => s.trim()).filter(Boolean);
      await onSave({ mode, tags });
      if (venue.venue_id) onPhotosSaved?.(photos);
      toast.success('Saved — photos live on the venue, ticket setting on this guide');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1.5 mb-2 ml-4 rounded-xl border border-fuchsia-500/30 bg-background/60 p-3 space-y-3" data-testid={`venue-media-panel-${venue.venue_id}`}>
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Photos — one URL per line, max 8 (first becomes the cover)</Label>
        {venue.venue_id ? (
          <textarea
            value={photosBuf}
            onChange={(e) => setPhotosBuf(e.target.value)}
            rows={3}
            placeholder={'https://…/photo1.jpg\nhttps://…/photo2.jpg'}
            className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-mono"
            data-testid={`venue-photos-input-${venue.venue_id}`}
          />
        ) : (
          <p className="text-[11px] text-muted-foreground italic">
            This venue comes from an event listing (no venue record yet) — its image comes from the event. Ticket setting below still works.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[11px] text-muted-foreground">Ticket banner</Label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs"
            data-testid={`venue-ticket-mode-${venue.venue_id}`}
          >
            <option value="auto">Auto — next upcoming event</option>
            <option value="tags">Only events matching tags…</option>
            <option value="hide">Hidden — no ticket banner</option>
          </select>
        </div>
        {mode === 'tags' && (
          <div className="space-y-1 flex-1 min-w-[180px]">
            <Label className="text-[11px] text-muted-foreground">Event tags / category (comma separated)</Label>
            <Input
              value={tagsBuf}
              onChange={(e) => setTagsBuf(e.target.value)}
              placeholder="oktoberfest, beer-festival"
              className="h-8 text-xs"
              data-testid={`venue-ticket-tags-${venue.venue_id}`}
            />
          </div>
        )}
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-white"
          data-testid={`venue-media-save-${venue.venue_id}`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Save</>}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Photos save to the venue itself (fixes grey boxes everywhere). The ticket setting applies to this guide only — &quot;Hidden&quot; removes the buy button, &quot;tags&quot; only shows an event whose tags or category match.
      </p>
    </div>
  );
}


// ===========================================================================
// EditGuideModal — edit metadata + curate venues (pin / exclude)
// ===========================================================================
// ---------------------------------------------------------------------------
// EventCardEditor — one row of the Cards tab. Own component so it can hold
// LOCAL text-input state for the multi-line fields (photos, addendum rows,
// recipe list fields). Without local state, splitting on every keystroke
// would clobber a trailing comma / newline the user just typed and make it
// impossible to type separators. We commit the parsed list back to the
// parent on blur AND on debounced idle.
// ---------------------------------------------------------------------------
function EventCardEditor({
  card, idx, totalCards, match, recipe, addendum,
  onUpdate, onUpdateRecipe, onMove, onRemove,
}) {
  const cardId = card.card_id;

  // Photos — one URL per line so the user can freely type commas without
  // triggering premature splitting. Local buffer keeps the raw text; on
  // blur we split, trim, drop empties, cap at 8 and commit to the card.
  const [photosBuf, setPhotosBuf] = useState(() => (card.photos || []).join('\n'));
  useEffect(() => {
    // Reflect external card.photos changes (e.g. reorder didn't touch photos).
    setPhotosBuf((card.photos || []).join('\n'));
  }, [cardId, card.photos]);
  const commitPhotos = () => {
    const list = photosBuf
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 8);
    onUpdate(cardId, { photos: list });
  };
  const appendPhotoUrls = (urls) => {
    const list = photosBuf
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .concat(urls)
      .slice(0, 8);
    setPhotosBuf(list.join('\n'));
    onUpdate(cardId, { photos: list });
  };
  const photoCount = photosBuf.split(/\r?\n/).map((s) => s.trim()).filter(Boolean).length;

  // Recipe list fields — same buffered pattern so typing commas works.
  const [typesBuf, setTypesBuf] = useState(() => (recipe.event_types || []).join(', '));
  const [tagsBuf,  setTagsBuf]  = useState(() => (recipe.tags_any   || []).join(', '));
  useEffect(() => {
    setTypesBuf((recipe.event_types || []).join(', '));
    setTagsBuf((recipe.tags_any || []).join(', '));
  }, [cardId, recipe.event_types, recipe.tags_any]);
  const commitTypes = () => onUpdateRecipe(cardId, {
    event_types: typesBuf.split(',').map((s) => s.trim()).filter(Boolean),
  });
  const commitTags  = () => onUpdateRecipe(cardId, {
    tags_any: tagsBuf.split(',').map((s) => s.trim()).filter(Boolean),
  });

  // Per-card addendum rows — arbitrary label/value pairs the admin can add.
  // Rendered UNDER the card image on the public page (mirrors venue guides
  // but fully custom per card instead of a shared 5-field vocabulary).
  const addRow = () => onUpdate(cardId, {
    addendum: [...addendum, { key: 'best_for', label: 'Best for', value: '' }],
  });
  const removeRow = (i) => onUpdate(cardId, {
    addendum: addendum.filter((_, idx2) => idx2 !== i),
  });
  const updateRow = (i, patch) => onUpdate(cardId, {
    addendum: addendum.map((r, idx2) => (idx2 === i ? { ...r, ...patch } : r)),
  });

  // Vibe tags — max 7 per card. Each tag has a link_type (venue/event/custom)
  // + a target that becomes the URL suffix (a slug/tag for venue|event, or a
  // raw URL for custom).
  const vibeTags = Array.isArray(card.vibe_tags) ? card.vibe_tags : [];
  const addVibeTag = () => {
    if (vibeTags.length >= 7) return;
    onUpdate(cardId, {
      vibe_tags: [...vibeTags, { label: '', link_type: 'venue', target: '' }],
    });
  };
  const updateVibeTag = (i, patch) => onUpdate(cardId, {
    vibe_tags: vibeTags.map((v, idx2) => (idx2 === i ? { ...v, ...patch } : v)),
  });
  const removeVibeTag = (i) => onUpdate(cardId, {
    vibe_tags: vibeTags.filter((_, idx2) => idx2 !== i),
  });

  // Custom CTA buttons — unlimited count per card. Rendered as a horizontal
  // row on the public page. Variant maps to a preset style.
  const buttons = Array.isArray(card.buttons) ? card.buttons : [];
  const addButton = () => onUpdate(cardId, {
    buttons: [...buttons, { label: '', url: '', variant: 'primary', open_in_new_tab: false }],
  });
  const updateButton = (i, patch) => onUpdate(cardId, {
    buttons: buttons.map((b, idx2) => (idx2 === i ? { ...b, ...patch } : b)),
  });
  const removeButton = (i) => onUpdate(cardId, {
    buttons: buttons.filter((_, idx2) => idx2 !== i),
  });

  return (
    <div
      className="rounded-xl border border-border/60 bg-card/50 p-4 space-y-3"
      data-testid={`event-card-editor-${cardId}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-indigo-300/70 tabular-nums w-8">
          {String(idx + 1).padStart(2, '0')}
        </span>
        <Input
          value={card.title}
          onChange={(e) => onUpdate(cardId, { title: e.target.value })}
          placeholder="Card title (e.g. Trivia Nights, Wine Tastings)"
          className="flex-1 font-semibold"
          data-testid={`card-title-${cardId}`}
        />
        <button
          type="button"
          onClick={() => onMove(cardId, -1)}
          className="p-1 text-muted-foreground hover:text-foreground"
          disabled={idx === 0}
          aria-label="Move up"
        >
          <ChevronRight className="h-4 w-4 -rotate-90" />
        </button>
        <button
          type="button"
          onClick={() => onMove(cardId, +1)}
          className="p-1 text-muted-foreground hover:text-foreground"
          disabled={idx === totalCards - 1}
          aria-label="Move down"
        >
          <ChevronRight className="h-4 w-4 rotate-90" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(cardId)}
          className="p-1 text-red-400 hover:text-red-300"
          aria-label="Remove card"
          data-testid={`card-remove-${cardId}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div>
        <Label className="text-[11px]">Description (renders under the card title)</Label>
        <RichTextEditor
          value={card.description_html || card.description || ''}
          onChange={(html) => {
            // Strip HTML tags for the plain-text fallback we still store
            // in `description`. Handles the rare case where the public
            // page needs a text-only excerpt.
            const plain = (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            onUpdate(cardId, { description_html: html || '', description: plain });
          }}
          placeholder="A short editorial paragraph about this category. Highlight key spots, tips, and what makes it uniquely Denver…"
          minHeight={140}
        />
        <p
          className="text-[10px] text-muted-foreground mt-0.5"
          data-testid={`card-desc-${cardId}`}
        >
          Bold, italic, links, headings, quotes, and lists supported.
        </p>
      </div>

      <div>
        <Label className="text-[11px] flex items-center gap-1.5">
          Photos <span className="text-muted-foreground font-normal">— one URL per line, max 8</span>
        </Label>
        <Textarea
          rows={3}
          value={photosBuf}
          onChange={(e) => setPhotosBuf(e.target.value)}
          onBlur={commitPhotos}
          className="font-mono text-[11px]"
          placeholder={"https://…/photo1.jpg\nhttps://…/photo2.jpg\nhttps://…/photo3.jpg"}
          data-testid={`card-photos-${cardId}`}
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-[10px] text-muted-foreground">
            Tip: paste one URL per line, or upload from your device. Photos commit when you tab or click away.
          </p>
          <PhotoUploadButton
            remaining={Math.max(0, 8 - photoCount)}
            onUploaded={appendPhotoUrls}
            testId={`card-photo-upload-${cardId}`}
          />
        </div>
      </div>

      {/* Recipe form — drives which live events surface as ticket banner */}
      <div className="rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/5 p-3 space-y-2">
        <Label className="text-[11px] text-fuchsia-200 flex items-center gap-1.5">
          <Wand2 className="h-3 w-3" /> Ticket-matching recipe
        </Label>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-[10px]">Event types (comma-separated)</Label>
            <Input
              value={typesBuf}
              onChange={(e) => setTypesBuf(e.target.value)}
              onBlur={commitTypes}
              placeholder="event, experience, reservation"
              className="text-xs"
              data-testid={`card-recipe-types-${cardId}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Vibe tags (comma-separated)</Label>
            <Input
              value={tagsBuf}
              onChange={(e) => setTagsBuf(e.target.value)}
              onBlur={commitTags}
              placeholder="trivia, live music, wine tasting"
              className="text-xs"
              data-testid={`card-recipe-tags-${cardId}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Date window</Label>
            <select
              value={recipe.date_window || ''}
              onChange={(e) => onUpdateRecipe(cardId, { date_window: e.target.value })}
              className="w-full h-8 text-xs rounded-md border border-input bg-background px-2"
              data-testid={`card-recipe-date-${cardId}`}
            >
              <option value="">All upcoming (evergreen)</option>
              <option value="this_weekend">This weekend</option>
              <option value="this_week">Next 7 days</option>
              <option value="this_month">Next 31 days</option>
              <option value="next_30">Next 30 days</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Price band</Label>
            <select
              value={recipe.price_band || ''}
              onChange={(e) => onUpdateRecipe(cardId, { price_band: e.target.value })}
              className="w-full h-8 text-xs rounded-md border border-input bg-background px-2"
              data-testid={`card-recipe-price-${cardId}`}
            >
              <option value="">Any price</option>
              <option value="$">$ · under $20</option>
              <option value="$$">$$ · $20-60</option>
              <option value="$$$">$$$ · $60-150</option>
              <option value="$$$$">$$$$ · $150+</option>
            </select>
          </div>
        </div>
      </div>

      {/* Under-photo custom info — arbitrary rows the admin controls */}
      <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-amber-200 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Under-photo info
            <span className="text-[9px] text-muted-foreground font-normal">
              (renders as a list under the card image on the public page)
            </span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRow}
            className="h-7 px-2 text-[10px] border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
            data-testid={`card-addendum-add-${cardId}`}
          >
            <Plus className="h-3 w-3 mr-1" /> Add row
          </Button>
        </div>
        {addendum.length === 0 ? (
          <p className="text-[10px] text-muted-foreground italic">
            No under-photo info yet. Click <b>Add row</b> to add lines like Location, Best for, Views of, Why we recommend, People are saying — or your own custom label.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid={`card-addendum-list-${cardId}`}>
            {addendum.map((row, i) => (
              <div key={i} className="flex gap-1.5 items-start">
                <select
                  value={row.key || 'custom'}
                  onChange={(e) => {
                    const preset = ADDENDUM_LABEL_PRESETS[e.target.value];
                    updateRow(i, {
                      key: e.target.value,
                      label: preset || row.label || '',
                    });
                  }}
                  className="w-24 h-8 text-[10px] rounded-md border border-input bg-background px-1.5 shrink-0"
                  data-testid={`card-addendum-icon-${cardId}-${i}`}
                >
                  <option value="location">📍 Location</option>
                  <option value="best_for">✨ Best for</option>
                  <option value="views_of">👁 Views of</option>
                  <option value="why_recommend">🏆 Why recommend</option>
                  <option value="people_saying">💬 People saying</option>
                  <option value="custom">⚙ Custom</option>
                </select>
                <Input
                  value={row.label || ''}
                  onChange={(e) => updateRow(i, { label: e.target.value })}
                  placeholder="Label"
                  className="h-8 text-xs w-40 shrink-0"
                  data-testid={`card-addendum-label-${cardId}-${i}`}
                />
                <Input
                  value={row.value || ''}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  placeholder="Value (what shows to readers)"
                  className="h-8 text-xs flex-1"
                  data-testid={`card-addendum-value-${cardId}-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="p-1 text-red-400 hover:text-red-300 shrink-0"
                  aria-label="Remove row"
                  data-testid={`card-addendum-remove-${cardId}-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vibe tags — max 7 per card, each links to a venue/event search
          or an arbitrary URL. Renders as pill buttons under the card
          description on the public page. */}
      <div className="rounded-lg border border-fuchsia-500/25 bg-fuchsia-500/5 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-fuchsia-200 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Vibe tags
            <span className="text-[9px] text-muted-foreground font-normal">
              (max 7 · pill buttons under the description)
            </span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addVibeTag}
            disabled={vibeTags.length >= 7}
            className="h-7 px-2 text-[10px] border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10 disabled:opacity-40"
            data-testid={`card-vibe-add-${cardId}`}
          >
            <Plus className="h-3 w-3 mr-1" /> Add vibe tag
          </Button>
        </div>
        {vibeTags.length === 0 ? (
          <p className="text-[10px] text-muted-foreground italic">
            No vibe tags yet — add tags like &quot;Date night&quot; or &quot;Free&quot; that link readers to matching venues or events.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid={`card-vibe-list-${cardId}`}>
            {vibeTags.map((v, i) => (
              <div key={i} className="flex gap-1.5 items-start">
                <Input
                  value={v.label || ''}
                  onChange={(e) => updateVibeTag(i, { label: e.target.value })}
                  placeholder="Vibe label (e.g. Date Night)"
                  className="h-8 text-xs flex-1"
                  data-testid={`card-vibe-label-${cardId}-${i}`}
                />
                <select
                  value={v.link_type || 'venue'}
                  onChange={(e) => updateVibeTag(i, { link_type: e.target.value })}
                  className="w-28 h-8 text-[10px] rounded-md border border-input bg-background px-1.5 shrink-0"
                  data-testid={`card-vibe-type-${cardId}-${i}`}
                >
                  <option value="venue">Venue search</option>
                  <option value="event">Event search</option>
                  <option value="custom">Custom URL</option>
                </select>
                <Input
                  value={v.target || ''}
                  onChange={(e) => updateVibeTag(i, { target: e.target.value })}
                  placeholder={
                    v.link_type === 'custom'
                      ? 'https://…'
                      : `Tag to search (defaults to label)`
                  }
                  className="h-8 text-xs w-44 shrink-0"
                  data-testid={`card-vibe-target-${cardId}-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeVibeTag(i)}
                  className="p-1 text-red-400 hover:text-red-300 shrink-0"
                  aria-label="Remove vibe tag"
                  data-testid={`card-vibe-remove-${cardId}-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom CTA buttons — unlimited per card. Rendered as a horizontal
          row on the public page below the vibe tags. */}
      <div className="rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] text-indigo-200 flex items-center gap-1.5">
            <ExternalLink className="h-3 w-3" /> Custom buttons
            <span className="text-[9px] text-muted-foreground font-normal">
              (up to 20 · rendered as a CTA row)
            </span>
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addButton}
            className="h-7 px-2 text-[10px] border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10"
            data-testid={`card-button-add-${cardId}`}
          >
            <Plus className="h-3 w-3 mr-1" /> Add button
          </Button>
        </div>
        {buttons.length === 0 ? (
          <p className="text-[10px] text-muted-foreground italic">
            No custom buttons yet — add CTAs like &quot;Reserve now&quot;, &quot;See the map&quot;, &quot;Follow the editor&quot;, etc.
          </p>
        ) : (
          <div className="space-y-1.5" data-testid={`card-buttons-list-${cardId}`}>
            {buttons.map((b, i) => (
              <div key={i} className="flex gap-1.5 items-start">
                <Input
                  value={b.label || ''}
                  onChange={(e) => updateButton(i, { label: e.target.value })}
                  placeholder="Button label (e.g. Reserve now)"
                  className="h-8 text-xs w-40 shrink-0"
                  data-testid={`card-button-label-${cardId}-${i}`}
                />
                <Input
                  value={b.url || ''}
                  onChange={(e) => updateButton(i, { url: e.target.value })}
                  placeholder="Destination URL (internal /path or https://…)"
                  className="h-8 text-xs flex-1"
                  data-testid={`card-button-url-${cardId}-${i}`}
                />
                <select
                  value={b.variant || 'primary'}
                  onChange={(e) => updateButton(i, { variant: e.target.value })}
                  className="w-24 h-8 text-[10px] rounded-md border border-input bg-background px-1.5 shrink-0"
                  data-testid={`card-button-variant-${cardId}-${i}`}
                >
                  <option value="primary">Primary</option>
                  <option value="outline">Outline</option>
                  <option value="ghost">Ghost</option>
                  <option value="amber">Amber</option>
                  <option value="indigo">Indigo</option>
                </select>
                <label
                  className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0 pt-1"
                  title="Open in new tab"
                >
                  <input
                    type="checkbox"
                    checked={!!b.open_in_new_tab}
                    onChange={(e) => updateButton(i, { open_in_new_tab: e.target.checked })}
                    data-testid={`card-button-newtab-${cardId}-${i}`}
                  />
                  ↗
                </label>
                <button
                  type="button"
                  onClick={() => removeButton(i)}
                  className="p-1 text-red-400 hover:text-red-300 shrink-0"
                  aria-label="Remove button"
                  data-testid={`card-button-remove-${cardId}-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Live match preview */}
      <div
        className={`rounded-lg border p-2.5 text-xs ${
          match.next
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-border/50 bg-muted/30'
        }`}
        data-testid={`card-match-preview-${cardId}`}
      >
        {match.next ? (
          <div className="flex items-center gap-2">
            <Ticket className="h-3.5 w-3.5 text-emerald-300 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-emerald-200 truncate">
                Next ticket on sale: {match.next.name}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {match.matches.length} matching event{match.matches.length === 1 ? '' : 's'} live · this banner will show above the card image.
              </p>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">
            <ImageIcon className="h-3 w-3 inline mr-1" />
            No live tickets match right now — the card will render without a banner. Adjust tags / date window to widen the match.
          </p>
        )}
      </div>
    </div>
  );
}

const ADDENDUM_LABEL_PRESETS = {
  location: 'Location',
  best_for: 'Best for',
  views_of: 'Views of',
  why_recommend: 'Why we recommend',
  people_saying: 'People are saying',
  custom: '',
};


function EditGuideModal({ guide, onClose, onSaved }) {
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState({
    title: guide.title || '',
    seo_title: guide.seo_title || '',
    slug: guide.slug || '',
    category_label: guide.category_label || '',
    cover_image: guide.cover_image || '',
    meta_description: guide.meta_description || '',
    intro_html: guide.intro_html || '',
  });
  // FAQ Q&A pairs — same field the public page + FAQPage JSON-LD read.
  const [faqs, setFaqs] = useState(() => (
    Array.isArray(guide.faqs) ? guide.faqs.map((f) => ({ q: f.q || '', a: f.a || '' })) : []
  ));
  const setFaq = (i, k, v) => setFaqs((cur) => cur.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const moveFaq = (i, dir) => setFaqs((cur) => {
    const t = i + dir;
    if (t < 0 || t >= cur.length) return cur;
    const n = [...cur];
    [n[i], n[t]] = [n[t], n[i]];
    return n;
  });
  const [featuredIds, setFeaturedIds] = useState(guide.featured_venue_ids || []);
  const [excludedIds, setExcludedIds] = useState(guide.excluded_venue_ids || []);
  // Editor's Top Picks — venue_ids that render the amber "Editor's Pick"
  // badge on the public guide.
  const [editorPicks, setEditorPicks] = useState(guide.editor_picks || []);
  // Explicit editor assignment — points at a `db.editor_profiles.user_id`
  // (real admin or a `guest_*` byline the admin created). Empty string
  // means "fall back to the auto-resolver (last_edited_by / author_id)".
  const [editorId, setEditorId] = useState(guide.editor_id || '');
  const [editorProfiles, setEditorProfiles] = useState([]);
  const [editorProfilesLoading, setEditorProfilesLoading] = useState(false);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState({
    display_name: '', blurb: '', avatar_url: '', instagram: '', website: '',
  });
  const [creatingGuest, setCreatingGuest] = useState(false);

  // Event-guide card list — only relevant when guide.subject_type === 'events'.
  // Each card: { card_id, title, description, photos:[], recipe:{event_types,
  //   tags_any, date_window, price_band}, pinned_event_ids:[] }.
  const isEventGuide = (guide.subject_type || 'venues') === 'events';
  const [eventCards, setEventCards] = useState(() => (
    // Backfill card_id on legacy cards so remove/reorder always work.
    Array.isArray(guide.event_cards)
      ? guide.event_cards.map((c, i) => ({
        ...c,
        card_id: c.card_id || `card_legacy_${i}_${Math.random().toString(36).slice(2, 8)}`,
      }))
      : []
  ));
  // Preview of live matching events per card (fetched from public GET).
  const [cardMatches, setCardMatches] = useState({});
  const [extraVenues, setExtraVenues] = useState([]); // venues editor explicitly pinned beyond the recipe match
  const [verifying, setVerifying] = useState(new Set());
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkVerifying, setBulkVerifying] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [mediaOpenId, setMediaOpenId] = useState(null);
  const [ticketOverrides, setTicketOverrides] = useState(guide.venue_ticket_overrides || {});

  const saveTicketOverride = async (venueId, ov) => {
    const next = { ...ticketOverrides, [venueId]: ov };
    await axios.put(
      `${API}/editorial-guides/${guide.guide_id}`,
      { venue_ticket_overrides: next },
      { headers: authHeaders() },
    );
    setTicketOverrides(next);
    guide.venue_ticket_overrides = next;
  };
  const [suggestForm, setSuggestForm] = useState({
    name: '', neighborhood: '', venue_type: 'restaurant', address: '', notes: '',
  });
  const [suggesting, setSuggesting] = useState(false);
  // Google Places fallback — kicks in when the in-DB search returns 0
  // hits OR when the editor explicitly clicks "Search Google Places".
  // Each hit can be one-click imported into our venues collection
  // already enriched (photos to CDN, AI description, lat/lng).
  const [placesResults, setPlacesResults] = useState([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesImporting, setPlacesImporting] = useState(null); // place_id being imported

  // Resolve the live recipe so the editor sees the exact venues that
  // would render on the public page. The admin preview path returns
  // drafts too, so this works for unpublished guides.
  useEffect(() => {
    if (tab !== 'venues') return;
    setLoadingVenues(true);
    const t = localStorage.getItem('eventism_token');
    axios.get(`${API}/public/guides/${guide.city}/${guide.slug}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => { setVenues(r.data?.venues || []); })
      .catch(() => { setVenues([]); })
      .finally(() => setLoadingVenues(false));
  }, [tab, guide.city, guide.slug]);

  // Load the full editor roster (real admins + guest bylines) when the
  // Editor tab is opened. Guarded so we don't re-fetch on every tab
  // switch — the list changes infrequently.
  useEffect(() => {
    if (tab !== 'editor' || editorProfiles.length > 0) return;
    setEditorProfilesLoading(true);
    axios.get(`${API}/admin/editor-profiles/list`, { headers: authHeaders() })
      .then((r) => setEditorProfiles(r.data?.profiles || []))
      .catch(() => toast.error('Failed to load editor list'))
      .finally(() => setEditorProfilesLoading(false));
  }, [tab, editorProfiles.length]);

  const createGuestEditor = async () => {
    const name = (guestForm.display_name || '').trim();
    if (name.length < 2) return toast.error('Enter a display name (2+ chars)');
    setCreatingGuest(true);
    try {
      const socials = {};
      if (guestForm.instagram.trim()) socials.instagram = guestForm.instagram.trim();
      if (guestForm.website.trim())   socials.website   = guestForm.website.trim();
      const r = await axios.post(
        `${API}/admin/editor-profiles/create-guest`,
        {
          display_name: name,
          blurb: guestForm.blurb || undefined,
          avatar_url: guestForm.avatar_url || undefined,
          social_links: Object.keys(socials).length ? socials : undefined,
        },
        { headers: authHeaders() },
      );
      const prof = r.data?.profile;
      if (prof?.user_id) {
        // Prepend so it's visible at the top of the dropdown immediately.
        setEditorProfiles((cur) => {
          const existing = cur.find((p) => p.user_id === prof.user_id);
          return existing ? cur : [prof, ...cur];
        });
        setEditorId(prof.user_id);
        toast.success(r.data?.created ? `Created byline "${prof.display_name}"` : `Byline exists — selected`);
        setShowGuestForm(false);
        setGuestForm({ display_name: '', blurb: '', avatar_url: '', instagram: '', website: '' });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create byline');
    } finally {
      setCreatingGuest(false);
    }
  };

  // ---- Event-card CRUD helpers ---------------------------------------
  const newBlankCard = () => ({
    card_id: `card_${Math.random().toString(36).slice(2, 12)}`,
    title: '',
    description: '',
    photos: [],
    recipe: { event_types: [], tags_any: [], date_window: '', price_band: '' },
    pinned_event_ids: [],
  });
  const addCard = () => setEventCards((cur) => [...cur, newBlankCard()]);
  const removeCard = (id) => setEventCards((cur) => cur.filter((c) => c.card_id !== id));
  const updateCard = (id, patch) => setEventCards((cur) =>
    cur.map((c) => (c.card_id === id ? { ...c, ...patch } : c)),
  );
  const updateCardRecipe = (id, patch) => setEventCards((cur) =>
    cur.map((c) => (c.card_id === id
      ? { ...c, recipe: { ...(c.recipe || {}), ...patch } }
      : c)),
  );
  const moveCard = (id, delta) => setEventCards((cur) => {
    const idx = cur.findIndex((c) => c.card_id === id);
    if (idx < 0) return cur;
    const next = idx + delta;
    if (next < 0 || next >= cur.length) return cur;
    const copy = [...cur];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  });

  // Fetch the LIVE matching events per card so the admin can see exactly
  // what will show above each image on the public page. Only for event
  // guides, and only on-demand when the Cards tab is active.
  useEffect(() => {
    if (!isEventGuide || tab !== 'venues') return;
    const t = localStorage.getItem('eventism_token');
    axios.get(`${API}/public/guides/${guide.city}/${guide.slug}`, {
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    })
      .then((r) => {
        const map = {};
        for (const c of (r.data?.event_cards || [])) {
          map[c.card_id] = {
            matches: c.matching_events || [],
            next: c.next_event || null,
          };
        }
        setCardMatches(map);
      })
      .catch(() => setCardMatches({}));
  }, [tab, isEventGuide, guide.city, guide.slug, eventCards.length]);

  const togglePin = (vid) => {
    if (!vid || typeof vid !== 'string') {
      toast.error('This venue has no ID yet — promote or save it first.');
      return;
    }
    setFeaturedIds((cur) => cur.includes(vid) ? cur.filter((x) => x !== vid) : [...cur, vid]);
    setExcludedIds((cur) => cur.filter((x) => x !== vid));
  };
  const toggleExclude = (vid) => {
    if (!vid || typeof vid !== 'string') {
      toast.error('This venue has no ID yet — promote or save it first.');
      return;
    }
    setExcludedIds((cur) => cur.includes(vid) ? cur.filter((x) => x !== vid) : [...cur, vid]);
    setFeaturedIds((cur) => cur.filter((x) => x !== vid));
    setEditorPicks((cur) => cur.filter((x) => x !== vid));
  };
  const togglePick = (vid) => {
    if (!vid || typeof vid !== 'string') {
      toast.error('This venue has no ID yet — promote or save it first.');
      return;
    }
    setEditorPicks((cur) => cur.includes(vid) ? cur.filter((x) => x !== vid) : [...cur, vid]);
    // A pick can never be excluded.
    setExcludedIds((cur) => cur.filter((x) => x !== vid));
  };

  const moveFeatured = (vid, delta) => setFeaturedIds((cur) => {
    const idx = cur.indexOf(vid);
    if (idx < 0) return cur;
    const next = idx + delta;
    if (next < 0 || next >= cur.length) return cur;
    const copy = [...cur];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  });

  const save = async () => {
    setSaving(true);
    try {
      // Defensive cleanup before hitting the wire:
      //  • Drop null/undefined/empty venue_ids (shadow venues from the
      //    event bridge have `venue_id: null` — those can't be persisted
      //    and would 422 the whole save).
      //  • Pull only the fields the backend's GuideUpdate model accepts;
      //    avoid leaking React-only state into the payload.
      const cleanIds = (arr) => Array.from(new Set(
        (arr || []).filter((x) => typeof x === 'string' && x.trim() !== '')
      ));
      const payload = {
        title: form.title,
        seo_title: (form.seo_title || '').trim(),
        slug: (form.slug || '').trim() || undefined,
        category_label: form.category_label,
        cover_image: form.cover_image,
        meta_description: form.meta_description,
        intro_html: form.intro_html,
        // Always send — an empty list legitimately clears all Q&As.
        faqs: (faqs || [])
          .map((f) => ({ q: (f.q || '').trim(), a: (f.a || '').trim() }))
          .filter((f) => f.q && f.a),
        featured_venue_ids: cleanIds(featuredIds),
        excluded_venue_ids: cleanIds(excludedIds),
        editor_picks: cleanIds(editorPicks),
        // Explicit editor byline assignment — empty string means "let the
        // backend fall back to last_edited_by / author_id".
        editor_id: (editorId || '').trim(),
      };
      // Only send event_cards for event guides — sending an empty array
      // on a venue guide would spuriously clear the field.
      if (isEventGuide) {
        payload.event_cards = (eventCards || []).map((c) => ({
          card_id: c.card_id,
          title: (c.title || '').trim(),
          description: (c.description || '').trim(),
          // Rich-text HTML from the TipTap editor. Empty string is fine —
          // the public page falls back to plain-text `description`.
          description_html: (c.description_html || '').trim(),
          photos: (c.photos || []).filter(Boolean),
          recipe: c.recipe || {},
          pinned_event_ids: c.pinned_event_ids || [],
          // Per-card custom under-photo rows. Trim to keep the DB clean.
          addendum: (Array.isArray(c.addendum) ? c.addendum : [])
            .map((r) => ({
              key: (r.key || 'custom').trim() || 'custom',
              label: (r.label || '').trim(),
              value: (r.value || '').trim(),
            }))
            .filter((r) => r.label && r.value),
          // Vibe pill tags — capped to 7. Empty label/target rows are dropped.
          vibe_tags: (Array.isArray(c.vibe_tags) ? c.vibe_tags : [])
            .map((v) => ({
              label: (v.label || '').trim(),
              link_type: v.link_type || 'venue',
              target: (v.target || '').trim(),
            }))
            .filter((v) => v.label)
            .slice(0, 7),
          // Custom CTA buttons — variants: primary/outline/ghost/amber/indigo.
          buttons: (Array.isArray(c.buttons) ? c.buttons : [])
            .map((b) => ({
              label: (b.label || '').trim(),
              url: (b.url || '').trim(),
              variant: b.variant || 'primary',
              open_in_new_tab: !!b.open_in_new_tab,
            }))
            .filter((b) => b.label && b.url),
        }));
      }
      await axios.put(`${API}/editorial-guides/${guide.guide_id}`, payload, {
        headers: authHeaders(),
      });
      toast.success('Saved');
      // Wrap the parent callback so an exception in the refetch chain
      // can't blank-screen the admin. Worst case we keep the modal open
      // with a toast instead of leaving the user staring at an empty page.
      try { onSaved(); }
      catch (e) {
        console.error('[edit-guide] onSaved threw:', e);
      }
    } catch (err) {
      // FastAPI returns `detail` as an array for 422 validation errors;
      // pulling .detail straight into toast.error renders [object Object]
      // (or crashes in StrictMode). Normalize to a string first.
      const raw = err?.response?.data?.detail;
      let msg = 'Save failed';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw))
        msg = raw.map((e) => e?.msg || JSON.stringify(e)).join('; ');
      else if (raw && typeof raw === 'object') msg = JSON.stringify(raw);
      console.error('[edit-guide] save failed', err);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-20 overflow-y-auto"
      data-testid="edit-guide-modal"
    >
      <Card className="w-full max-w-3xl border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold font-['Space_Grotesk']">Edit guide</h2>
              <p className="text-xs text-muted-foreground">{guide.city} · {guide.slug}</p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              data-testid="edit-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 mb-5 border-b border-border">
            {[
              { id: 'details', label: 'Details' },
              { id: 'editor',  label: 'Editor' },
              { id: 'venues',  label: isEventGuide ? 'Cards' : 'Venues' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`edit-tab-${t.id}`}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                  tab === t.id
                    ? 'border-fuchsia-400 text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'details' && (
            <div className="space-y-4" data-testid="edit-details-tab">
              <div>
                <Label className="text-xs">Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="edit-title"
                />
              </div>
              <div>
                <Label className="text-xs">URL slug — /{guide.city}/guides/…</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="thanksgiving-in-denver-2026"
                  className="font-mono text-xs"
                  data-testid="edit-slug"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Lowercase + hyphens; must be unique in {guide.city}. Changing it changes the public URL — old links will 404 unless you add a redirect.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Category label</Label>
                  <Input
                    value={form.category_label}
                    onChange={(e) => setForm({ ...form, category_label: e.target.value })}
                    data-testid="edit-category-label"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cover image</Label>
                  <CoverImageUploader
                    value={form.cover_image}
                    onChange={(url) => setForm({ ...form, cover_image: url })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">SEO title (the &lt;title&gt; tag — falls back to the H1 title)</Label>
                <Input
                  value={form.seo_title}
                  onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                  placeholder="e.g. 12 Best Karaoke Bars in Denver (2026 Guide)"
                  data-testid="edit-seo-title"
                />
              </div>
              <div>
                <Label className="text-xs">Meta description (SEO)</Label>
                <Textarea
                  value={form.meta_description}
                  onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                  rows={2}
                  data-testid="edit-meta"
                />
              </div>
              <div>
                <Label className="text-xs">Intro</Label>
                <div className="border border-border rounded-lg overflow-hidden" data-testid="edit-intro-rte">
                  <RichTextEditor
                    value={form.intro_html}
                    onChange={(html) => setForm({ ...form, intro_html: html })}
                    placeholder="Lead paragraph for the guide…"
                    minHeight={180}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Use the toolbar for bold / italic / links / lists / headings. Hit
                  the <code className="text-fuchsia-300">HTML</code> button to edit raw markup. Re-gen AI on the
                  list will overwrite this.
                </p>
              </div>
              <div>
                <Label className="text-xs">FAQs ({faqs.length}) — public FAQ section + Google FAQ schema</Label>
                <div className="space-y-2 mt-1" data-testid="edit-faqs">
                  {faqs.map((f, i) => (
                    <div key={i} className="rounded-lg border border-border p-3 flex items-start gap-2" data-testid={`faq-row-${i}`}>
                      <div className="flex-1 space-y-2">
                        <Input
                          value={f.q}
                          placeholder={`Question ${i + 1}`}
                          onChange={(e) => setFaq(i, 'q', e.target.value)}
                          data-testid={`faq-q-${i}`}
                        />
                        <Textarea
                          value={f.a}
                          rows={2}
                          placeholder="Answer (1–2 sentences)"
                          onChange={(e) => setFaq(i, 'a', e.target.value)}
                          data-testid={`faq-a-${i}`}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0} onClick={() => moveFaq(i, -1)} data-testid={`faq-up-${i}`}>
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === faqs.length - 1} onClick={() => moveFaq(i, 1)} data-testid={`faq-down-${i}`}>
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-400" onClick={() => setFaqs((cur) => cur.filter((_, j) => j !== i))} data-testid={`faq-delete-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setFaqs((cur) => [...cur, { q: '', a: '' }])} data-testid="faq-add-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" />Add Q&amp;A
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    Rows with an empty question or answer are dropped on save. "Re-gen AI" overwrites these with 5 fresh Q&amp;As.
                  </p>
                </div>
              </div>
            </div>
          )}

          {tab === 'editor' && (
            <div className="space-y-4" data-testid="edit-editor-tab">
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3">
                <div className="flex items-start gap-2">
                  <UserIcon className="h-4 w-4 text-fuchsia-300 mt-0.5" />
                  <div className="text-xs text-muted-foreground">
                    <p className="text-fuchsia-200 font-semibold mb-0.5">Byline &amp; attribution</p>
                    Pick who this guide is credited to. Choose an existing editor or create a byline for a guest/freelance writer publishing through Eventism.
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-2">
                  <UserIcon className="h-3 w-3" /> Attribute this guide to
                </Label>
                {editorProfilesLoading ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading editors…
                  </div>
                ) : (
                  <select
                    value={editorId}
                    onChange={(e) => setEditorId(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="editor-select"
                  >
                    <option value="">— Default (whoever last edited) —</option>
                    {editorProfiles.map((p) => (
                      <option key={p.user_id} value={p.user_id}>
                        {p.display_name}
                        {p.is_guest ? ' · guest byline' : ''}
                      </option>
                    ))}
                  </select>
                )}
                {editorId && (() => {
                  const p = editorProfiles.find((x) => x.user_id === editorId);
                  if (!p) return null;
                  return (
                    <div
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/60 bg-card"
                      data-testid="editor-preview"
                    >
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {(p.display_name || '?').slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {p.display_name}
                          {p.is_guest && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-normal">
                              Guest byline
                            </span>
                          )}
                        </p>
                        {p.blurb && (
                          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{p.blurb}</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="pt-2 border-t border-border">
                {!showGuestForm ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowGuestForm(true)}
                    className="border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                    data-testid="add-guest-byline-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add a guest byline
                  </Button>
                ) : (
                  <div
                    className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-3"
                    data-testid="guest-byline-form"
                  >
                    <div className="flex items-center justify-between">
                      <Label className="text-fuchsia-200 text-xs font-semibold flex items-center gap-1.5">
                        <Sparkles className="h-3 w-3" /> New guest byline
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowGuestForm(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <div className="sm:col-span-2 space-y-1">
                        <Label className="text-[11px]">Display name *</Label>
                        <Input
                          value={guestForm.display_name}
                          onChange={(e) => setGuestForm({ ...guestForm, display_name: e.target.value })}
                          placeholder="e.g. Alex Rivera"
                          data-testid="guest-name-input"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1">
                        <Label className="text-[11px]">Short blurb</Label>
                        <Textarea
                          rows={2}
                          value={guestForm.blurb}
                          onChange={(e) => setGuestForm({ ...guestForm, blurb: e.target.value })}
                          placeholder="Denver-based food writer covering cocktails and low-key wine bars."
                          data-testid="guest-blurb-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Avatar URL</Label>
                        <Input
                          value={guestForm.avatar_url}
                          onChange={(e) => setGuestForm({ ...guestForm, avatar_url: e.target.value })}
                          placeholder="https://…/headshot.jpg"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px]">Instagram</Label>
                        <Input
                          value={guestForm.instagram}
                          onChange={(e) => setGuestForm({ ...guestForm, instagram: e.target.value })}
                          placeholder="@handle or full URL"
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-1">
                        <Label className="text-[11px]">Website / portfolio</Label>
                        <Input
                          value={guestForm.website}
                          onChange={(e) => setGuestForm({ ...guestForm, website: e.target.value })}
                          placeholder="alexrivera.com"
                        />
                      </div>
                    </div>
                    <Button
                      onClick={createGuestEditor}
                      disabled={creatingGuest || !guestForm.display_name.trim()}
                      className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold"
                      data-testid="guest-create-btn"
                    >
                      {creatingGuest
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
                        : <><Save className="h-4 w-4 mr-2" /> Create &amp; assign byline</>}
                    </Button>
                    <p className="text-[10px] text-muted-foreground">
                      Guest bylines have no login — they exist purely to attribute a published guide. You can reuse them across future guides.
                    </p>
                  </div>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground pt-1">
                Don’t see the writer? Click <b>Add a guest byline</b> above. Save the guide when done — the byline will appear on the published page.
              </p>
            </div>
          )}

          {tab === 'venues' && isEventGuide && (
            <div className="space-y-4" data-testid="edit-event-cards-tab">
              <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3">
                <div className="flex items-start gap-2">
                  <Ticket className="h-4 w-4 text-indigo-300 mt-0.5" />
                  <div className="text-xs text-muted-foreground">
                    <p className="text-indigo-200 font-semibold mb-0.5">Event cards</p>
                    Each card is an evergreen editorial block (title, description, photos). The <b>recipe</b> below each card matches live ticketed events — when one is on sale, it shows as a banner above the card image on the public page. When no event matches, the card renders without the banner.
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {eventCards.length} card{eventCards.length === 1 ? '' : 's'} · reorder with ↑ ↓
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addCard}
                  className="border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10"
                  data-testid="add-card-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add card
                </Button>
              </div>

              {eventCards.length === 0 && (
                <div className="text-center text-xs text-muted-foreground py-8 border border-dashed border-border/50 rounded-lg">
                  No cards yet. Click <b>Add card</b> to create your first one.
                </div>
              )}

              <div className="space-y-3">
                {eventCards.map((card, idx) => {
                  const match = cardMatches[card.card_id] || { matches: [], next: null };
                  const recipe = card.recipe || {};
                  const addendum = Array.isArray(card.addendum) ? card.addendum : [];
                  return (
                    <EventCardEditor
                      key={card.card_id}
                      card={card}
                      idx={idx}
                      totalCards={eventCards.length}
                      match={match}
                      recipe={recipe}
                      addendum={addendum}
                      onUpdate={updateCard}
                      onUpdateRecipe={updateCardRecipe}
                      onMove={moveCard}
                      onRemove={removeCard}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'venues' && !isEventGuide && (
            <div data-testid="edit-venues-tab">
              <div className="flex items-start justify-between gap-3 mb-3">
                <p className="text-xs text-muted-foreground flex-1">
                  Live recipe result. <Pin className="inline h-3 w-3 text-fuchsia-300" /> pins
                  a venue to the top; <Ban className="inline h-3 w-3 text-red-300" /> excludes
                  it; <ShieldCheck className="inline h-3 w-3 text-emerald-300" /> verifies via Google Places.
                  {' '}{featuredIds.length} pinned · {excludedIds.length} excluded ·
                  {' '}{venues.filter((v) => v.ai_generated).length} AI · {venues.filter((v) => v.verified).length} verified.
                </p>
                {venues.some((v) => v.ai_generated) && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={bulkVerifying}
                    onClick={async () => {
                      setBulkVerifying(true);
                      try {
                        const r = await axios.post(
                          `${API}/admin/venues/bulk-verify-places`,
                          { city: guide.city, only_ai_generated: true, limit: 20 },
                          { headers: authHeaders(), timeout: 120000 },
                        );
                        const d = r.data || {};
                        if (d.quota_blocked) {
                          toast.error('Google Places daily quota hit — try again tomorrow.');
                        } else {
                          toast.success(`Verified ${d.verified || 0} · ${d.no_match || 0} no-match · ${d.errors || 0} errors`);
                        }
                        // Re-fetch the venues so the UI reflects new statuses.
                        const t = localStorage.getItem('eventism_token');
                        const rr = await axios.get(`${API}/public/guides/${guide.city}/${guide.slug}`, {
                          headers: t ? { Authorization: `Bearer ${t}` } : {},
                        });
                        setVenues(rr.data?.venues || []);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Bulk verify failed');
                      } finally { setBulkVerifying(false); }
                    }}
                    className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex-shrink-0"
                    data-testid="edit-venues-bulk-verify"
                  >
                    {bulkVerifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                    Verify all
                  </Button>
                )}
              </div>
              {loadingVenues ? (
                <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-fuchsia-300" /></div>
              ) : venues.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  Recipe matched 0 venues for {guide.city}.<br />
                  Edit the recipe in your seed script or wait for venues to be added.
                </div>
              ) : (
                <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                  {venues.map((v) => {
                    const ovKey = v.venue_id || v.slug;
                    const pinned = featuredIds.includes(v.venue_id);
                    const excluded = excludedIds.includes(v.venue_id);
                    const pick = editorPicks.includes(v.venue_id);
                    return (
                      <div key={ovKey}>
                      <div
                        className={`flex items-center gap-3 p-2.5 rounded-xl border transition-colors ${
                          excluded ? 'border-red-500/30 bg-red-500/5 opacity-60'
                          : pinned ? 'border-fuchsia-500/40 bg-fuchsia-500/5'
                          : 'border-border'
                        }`}
                        data-testid={`edit-venue-${v.venue_id}`}
                      >
                        {v.image_url || (v.photos && v.photos[0]) ? (
                          <img
                            src={v.image_url || v.photos[0]}
                            alt={v.name}
                            className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-sm truncate">{v.name}</p>
                            {v.verified && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-bold uppercase tracking-widest">
                                <ShieldCheck className="h-2.5 w-2.5" /> Verified
                              </span>
                            )}
                            {v.ai_generated && !v.verified && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[9px] font-bold uppercase tracking-widest">
                                <Sparkles className="h-2.5 w-2.5" /> AI
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {v.neighborhood || v.city}{v.venue_type ? ` · ${titleize(v.venue_type)}` : ''}
                          </p>
                        </div>
                        {v.ai_generated && !v.verified && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={verifying.has(v.venue_id)}
                            onClick={async () => {
                              setVerifying((cur) => new Set(cur).add(v.venue_id));
                              try {
                                const r = await axios.post(
                                  `${API}/admin/venues/${v.venue_id}/verify-places`,
                                  {},
                                  { headers: authHeaders(), timeout: 30000 },
                                );
                                const status = r.data?.status;
                                if (status === 'verified') {
                                  toast.success(`Verified · ${r.data?.matched}`);
                                  setVenues((cur) => cur.map((x) => x.venue_id === v.venue_id
                                    ? { ...x, ai_generated: false, verified: true }
                                    : x));
                                } else if (status === 'quota_exceeded') {
                                  toast.error('Google Places quota hit — try tomorrow.');
                                } else if (status === 'no_match') {
                                  toast.message(`No match on Google for "${v.name}"`);
                                } else {
                                  toast.error(r.data?.reason || 'Verify failed');
                                }
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Verify failed');
                              } finally {
                                setVerifying((cur) => {
                                  const n = new Set(cur);
                                  n.delete(v.venue_id);
                                  return n;
                                });
                              }
                            }}
                            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`edit-venue-verify-${v.venue_id}`}
                          >
                            {verifying.has(v.venue_id)
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <ShieldCheck className="h-3.5 w-3.5" />}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={mediaOpenId === ovKey ? 'default' : 'outline'}
                          onClick={() => setMediaOpenId(mediaOpenId === ovKey ? null : ovKey)}
                          className={mediaOpenId === ovKey ? 'bg-indigo-500 hover:bg-indigo-400 text-white' : 'border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10'}
                          data-testid={`edit-venue-media-${ovKey}`}
                          title="Photos & ticket banner"
                        >
                          <ImageIcon className="h-3.5 w-3.5" />
                        </Button>
                        {v.venue_id ? (
                        <>
                        {pinned && (
                          <div className="flex flex-col items-center -my-1">
                            <button type="button" onClick={() => moveFeatured(v.venue_id, -1)} className="text-muted-foreground hover:text-fuchsia-300 p-0.5" data-testid={`edit-venue-pinup-${v.venue_id}`} title="Move pinned card up">
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <span className="text-[9px] font-bold text-fuchsia-300 tabular-nums leading-none">{featuredIds.indexOf(v.venue_id) + 1}</span>
                            <button type="button" onClick={() => moveFeatured(v.venue_id, 1)} className="text-muted-foreground hover:text-fuchsia-300 p-0.5" data-testid={`edit-venue-pindown-${v.venue_id}`} title="Move pinned card down">
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                        <Button
                          size="sm"
                          variant={pick ? 'default' : 'outline'}
                          onClick={() => togglePick(v.venue_id)}
                          className={pick ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'border-amber-500/30 text-amber-300 hover:bg-amber-500/10'}
                          data-testid={`edit-venue-pick-${v.venue_id}`}
                          title={pick ? "Remove Editor's Pick badge" : "Mark as Editor's Pick"}
                        >
                          <Star className={`h-3.5 w-3.5 ${pick ? 'fill-black' : ''}`} />
                        </Button>
                        <Button
                          size="sm"
                          variant={pinned ? 'default' : 'outline'}
                          onClick={() => togglePin(v.venue_id)}
                          className={pinned ? 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white' : ''}
                          data-testid={`edit-venue-pin-${v.venue_id}`}
                        >
                          {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
                        </Button>
                        <Button
                          size="sm"
                          variant={excluded ? 'default' : 'outline'}
                          onClick={() => toggleExclude(v.venue_id)}
                          className={excluded ? 'bg-red-500 hover:bg-red-400 text-white' : ''}
                          data-testid={`edit-venue-exclude-${v.venue_id}`}
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                        </>
                        ) : (
                          <span
                            className="text-[9px] font-bold uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2 py-1 whitespace-nowrap"
                            title="Rows without a venue ID are automatically left off the public page — no action needed"
                            data-testid={`edit-venue-noid-${ovKey}`}
                          >
                            Not published
                          </span>
                        )}
                      </div>
                      {mediaOpenId === ovKey && (
                        <VenuePhotoTicketPanel
                          venue={v}
                          override={ticketOverrides[ovKey]}
                          onSave={(ov) => saveTicketOverride(ovKey, ov)}
                          onPhotosSaved={(photos) => setVenues((cur) => cur.map((x) => (
                            x.venue_id === v.venue_id
                              ? { ...x, photos, image_url: photos[0] || x.image_url }
                              : x
                          )))}
                        />
                      )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ─── Under-photo editorial addendum (5 optional toggles) ─── */}
              <UnderPhotoAddendumPanel
                guide={guide}
                venues={venues}
                onSaved={(fields, venue_addendum) => {
                  // Refresh local view of the panel + let parent re-fetch
                  // if it wants; the panel itself owns the toast + spinner.
                  guide.venue_addendum_fields = fields;
                  guide.venue_addendum = venue_addendum;
                }}
              />

              {/* ─── Add a venue we have / suggest a new one ─── */}
              <div className="mt-5 pt-4 border-t border-border space-y-3" data-testid="edit-venues-add-panel">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Add another venue
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSuggest((s) => !s)}
                    data-testid="edit-venues-suggest-toggle"
                  >
                    {showSuggest ? 'Hide form' : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Suggest new venue</>}
                  </Button>
                </div>

                {/* Search existing venues */}
                <div className="relative">
                  <Input
                    value={searchQ}
                    onChange={async (e) => {
                      const q = e.target.value;
                      setSearchQ(q);
                      if (!q.trim()) { setSearchResults([]); return; }
                      setSearching(true);
                      try {
                        const r = await axios.get(
                          `${API}/cities/${guide.city}/venues/search/lookup`,
                          { params: { q, limit: 8 }, headers: authHeaders() },
                        );
                        setSearchResults(r.data?.venues || []);
                      } catch { setSearchResults([]); }
                      finally { setSearching(false); }
                    }}
                    placeholder={`Search venues in ${guide.city}…`}
                    data-testid="edit-venues-search"
                  />
                  {searching && (
                    <Loader2 className="h-4 w-4 absolute right-3 top-3 animate-spin text-muted-foreground" />
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="edit-venues-search-results">
                    {searchResults.map((r) => {
                      const already = venues.some((v) => v.venue_id === r.venue_id);
                      return (
                        <div
                          key={r.venue_id}
                          className="flex items-center gap-2 p-2 rounded-lg border border-border hover:border-fuchsia-400/40"
                          data-testid={`edit-venues-result-${r.venue_id}`}
                        >
                          {r.image_url || (r.photos && r.photos[0]) ? (
                            <img src={r.image_url || r.photos[0]} alt={r.name} className="h-9 w-9 rounded-md object-cover" />
                          ) : (
                            <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center">
                              <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold truncate">{r.name}</p>
                            <p className="text-[11px] text-muted-foreground truncate">
                              {r.neighborhood || titleize(r.venue_type || '')}
                              {r.verified && ' · verified'}
                              {r.ai_generated && ' · AI'}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant={already ? 'outline' : 'default'}
                            disabled={already}
                            onClick={() => {
                              if (already) return;
                              setFeaturedIds((cur) => cur.includes(r.venue_id) ? cur : [...cur, r.venue_id]);
                              setVenues((cur) => [...cur, { ...r, _editor_pinned: true }]);
                              setSearchQ('');
                              setSearchResults([]);
                              toast.success(`Pinned ${r.name} to this guide`);
                            }}
                            className={already ? '' : 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white'}
                            data-testid={`edit-venues-pin-search-${r.venue_id}`}
                          >
                            {already ? 'Already in' : <><Pin className="h-3.5 w-3.5 mr-1.5" /> Pin</>}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {searchQ.trim() && !searching && searchResults.length === 0 && (
                  <div className="space-y-2" data-testid="edit-venues-search-empty">
                    <p className="text-xs text-muted-foreground">
                      No matches in {guide.city}. Try searching Google Places
                      to add a brand-new verified venue:
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs text-fuchsia-300 border-fuchsia-400/40 hover:bg-fuchsia-500/10"
                      disabled={placesLoading}
                      onClick={async () => {
                        setPlacesLoading(true);
                        try {
                          const r = await axios.get(
                            `${API}/places/autocomplete`,
                            { params: { q: `${searchQ} ${guide.city}` },
                              headers: authHeaders() },
                          );
                          setPlacesResults(r.data?.results || []);
                          if (r.data?.error === 'quota_exceeded') {
                            toast.error(r.data?.message || 'Google Places daily quota reached — try again tomorrow.');
                          } else if (!(r.data?.results || []).length) {
                            toast.message('No Google Places matches either — try the manual "Suggest" form.');
                          }
                        } catch (e) {
                          toast.error('Google Places lookup failed');
                          setPlacesResults([]);
                        } finally {
                          setPlacesLoading(false);
                        }
                      }}
                      data-testid="edit-venues-search-google-btn"
                    >
                      {placesLoading
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      Search Google Places
                    </Button>
                  </div>
                )}

                {/* Google Places results — one-click import & pin */}
                {placesResults.length > 0 && (
                  <div className="space-y-1.5 max-h-72 overflow-y-auto rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-2" data-testid="edit-venues-places-results">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-300 px-1 pb-1 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Google Places matches
                    </p>
                    {placesResults.map((p) => (
                      <div
                        key={p.place_id}
                        className="flex items-center gap-2 p-2 rounded-lg border border-border bg-background/40 hover:border-fuchsia-400/40"
                        data-testid={`edit-venues-places-${p.place_id}`}
                      >
                        <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-3.5 w-3.5 text-fuchsia-300" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate">{p.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">
                            {p.formatted_address}
                            {p.rating && ` · ★ ${p.rating.toFixed(1)}`}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          disabled={placesImporting === p.place_id}
                          onClick={async () => {
                            setPlacesImporting(p.place_id);
                            const t = toast.loading(`Importing ${p.name} & adding to guide…`);
                            try {
                              const res = await axios.post(
                                `${API}/admin/venues/import-from-places`,
                                { place_id: p.place_id, city: guide.city },
                                { headers: authHeaders() },
                              );
                              const v = res.data?.venue;
                              if (v && v.venue_id) {
                                setFeaturedIds((cur) => cur.includes(v.venue_id) ? cur : [...cur, v.venue_id]);
                                setVenues((cur) => [...cur, { ...v, _editor_pinned: true }]);
                                toast.success(
                                  `Imported "${v.name}" — ${(v.photos || []).length} photo(s) saved, pinned to guide.`,
                                  { id: t },
                                );
                                setSearchQ('');
                                setSearchResults([]);
                                setPlacesResults([]);
                              } else {
                                toast.error('Import returned no venue', { id: t });
                              }
                            } catch (err) {
                              toast.error(
                                err?.response?.data?.detail || 'Google Places import failed',
                                { id: t },
                              );
                            } finally {
                              setPlacesImporting(null);
                            }
                          }}
                          className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
                          data-testid={`edit-venues-places-import-${p.place_id}`}
                        >
                          {placesImporting === p.place_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Import &amp; Pin</>}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Suggest new venue form */}
                {showSuggest && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="edit-venues-suggest-form">
                    <p className="text-xs text-amber-300 font-bold uppercase tracking-widest">
                      Suggest a new venue for {titleize(guide.city)}
                    </p>
                    <Input
                      value={suggestForm.name}
                      onChange={(e) => setSuggestForm({ ...suggestForm, name: e.target.value })}
                      placeholder="Venue name *"
                      data-testid="edit-venues-suggest-name"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={suggestForm.neighborhood}
                        onChange={(e) => setSuggestForm({ ...suggestForm, neighborhood: e.target.value })}
                        placeholder="Neighborhood"
                        data-testid="edit-venues-suggest-nbhd"
                      />
                      <Input
                        value={suggestForm.venue_type}
                        onChange={(e) => setSuggestForm({ ...suggestForm, venue_type: e.target.value })}
                        placeholder="restaurant / cocktail_bar / …"
                        data-testid="edit-venues-suggest-type"
                      />
                    </div>
                    <Input
                      value={suggestForm.address}
                      onChange={(e) => setSuggestForm({ ...suggestForm, address: e.target.value })}
                      placeholder="Street address (optional)"
                      data-testid="edit-venues-suggest-address"
                    />
                    <Textarea
                      value={suggestForm.notes}
                      onChange={(e) => setSuggestForm({ ...suggestForm, notes: e.target.value })}
                      placeholder="Why is this venue a good fit? (optional)"
                      rows={2}
                      data-testid="edit-venues-suggest-notes"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => setShowSuggest(false)}>Cancel</Button>
                      <Button
                        size="sm"
                        disabled={suggesting || !suggestForm.name.trim()}
                        onClick={async () => {
                          setSuggesting(true);
                          try {
                            const r = await axios.post(
                              `${API}/cities/venues/suggest`,
                              { ...suggestForm, city: guide.city },
                              { headers: authHeaders() },
                            );
                            const v = r.data?.venue;
                            if (v) {
                              setFeaturedIds((cur) => [...cur, v.venue_id]);
                              setVenues((cur) => [...cur, { ...v, _editor_pinned: true }]);
                              setSuggestForm({ name: '', neighborhood: '', venue_type: 'restaurant', address: '', notes: '' });
                              setShowSuggest(false);
                              toast.success(`Added "${v.name}" — marked as suggested for admin review.`);
                            }
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || 'Failed to create venue');
                          } finally { setSuggesting(false); }
                        }}
                        className="bg-amber-500 hover:bg-amber-400 text-black"
                        data-testid="edit-venues-suggest-submit"
                      >
                        {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add + pin'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
              data-testid="edit-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// MatrixGenerateModal — cartesian product across cities × intents × cats etc.
// ===========================================================================
function MatrixGenerateModal({ onClose, onDone }) {
  const [city, setCity] = useState('Denver, New York, Chicago, Los Angeles');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('best_category_in_city');
  const [categories, setCategories] = useState('Wine Spots, Rooftop Bars, Cocktail Bars');
  const [intents, setIntents] = useState('Best, Hidden');
  const [neighborhoods, setNeighborhoods] = useState('');
  const [seasons, setSeasons] = useState('');
  const [aiOn, setAiOn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/editorial-guides/_templates`, { headers: authHeaders() })
      .then((r) => setTemplates(r.data?.templates || []))
      .catch(() => {});
  }, []);

  const csv = (s) => (s || '').split(',').map((t) => t.trim()).filter(Boolean);

  const cities = csv(city);
  const cats = csv(categories);
  const intentsArr = csv(intents);
  const hoods = csv(neighborhoods);
  const seasonsArr = csv(seasons);
  const combos =
    Math.max(1, cities.length) *
    Math.max(1, cats.length) *
    Math.max(1, intentsArr.length) *
    Math.max(1, hoods.length) *
    Math.max(1, seasonsArr.length);

  const submit = async () => {
    if (!cities.length) return toast.error('At least one city');
    if (combos > 96) return toast.error(`Matrix is ${combos} combos — cap is 96. Narrow the axes.`);
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/editorial-guides/matrix/generate`,
        {
          cities, template_id: templateId,
          categories: cats.length ? cats : null,
          intents: intentsArr.length ? intentsArr : null,
          neighborhoods: hoods.length ? hoods : null,
          seasons: seasonsArr.length ? seasonsArr : null,
          auto_generate_ai: aiOn,
        },
        { headers: authHeaders() },
      );
      const c = r.data?.created?.length || 0;
      const s = r.data?.skipped?.length || 0;
      toast.success(`Matrix created ${c} draft${c === 1 ? '' : 's'}${s ? ` · ${s} skipped` : ''}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Matrix generate failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-20 overflow-y-auto" data-testid="matrix-modal">
      <Card className="max-w-3xl w-full border-indigo-500/30 bg-card">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h2 className="text-xl font-bold font-['Space_Grotesk']">Matrix generate</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Cartesian product: cities × intents × categories × neighborhoods × seasons.
                Hard cap of 96 combos. AI is OFF by default to protect your LLM budget.
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="matrix-close"><X className="h-5 w-5" /></button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Cities (comma separated slugs OR names)</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="matrix-cities" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Template</Label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full h-10 rounded-md border border-border bg-card px-3 text-sm"
                data-testid="matrix-template"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}{t.needs_category ? ' · needs categories' : ''}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Categories</Label>
              <Input value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="Wine Spots, Rooftop Bars" data-testid="matrix-categories" />
            </div>
            <div className="space-y-1.5">
              <Label>Intents</Label>
              <Input value={intents} onChange={(e) => setIntents(e.target.value)} placeholder="Best, Top, Hidden, Romantic" data-testid="matrix-intents" />
            </div>
            <div className="space-y-1.5">
              <Label>Neighborhoods (optional)</Label>
              <Input value={neighborhoods} onChange={(e) => setNeighborhoods(e.target.value)} placeholder="RiNo, Williamsburg, Wicker Park" data-testid="matrix-hoods" />
            </div>
            <div className="space-y-1.5">
              <Label>Seasons (optional)</Label>
              <Input value={seasons} onChange={(e) => setSeasons(e.target.value)} placeholder="Halloween, Valentine's" data-testid="matrix-seasons" />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={aiOn} onChange={(e) => setAiOn(e.target.checked)} data-testid="matrix-ai-toggle" />
              Also AI-generate intro + FAQs (slower, consumes LLM credits)
            </label>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              <strong className={combos > 96 ? 'text-red-300' : 'text-fuchsia-300'}>{combos}</strong> draft{combos === 1 ? '' : 's'} will be created.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting || combos > 96}
                className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
                data-testid="matrix-submit"
              >
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <>Generate matrix</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// SuggestionsPanel — what to publish next, ranked by venue+event signal.
// ===========================================================================
function SuggestionsPanel({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState('');

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/editorial-guides/suggestions/list`, { headers: authHeaders() });
      setRows(r.data?.suggestions || []);
    } catch {
      toast.error('Failed to load suggestions');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSuggestions(); }, []);

  const generate = async (s) => {
    const key = `${s.city}-${s.tag}`;
    setCreating(key);
    try {
      await axios.post(
        `${API}/editorial-guides/bulk-generate`,
        {
          city: s.city,
          auto_generate_ai: true,
          items: [{ template_id: s.suggested_template, category_label: s.suggested_category_label }],
        },
        { headers: authHeaders() },
      );
      toast.success(`Draft created: ${s.suggested_title}`);
      setRows((r) => r.filter((x) => `${x.city}-${x.tag}` !== key));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generate failed');
    } finally { setCreating(''); }
  };

  return (
    <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent mb-6" data-testid="suggestions-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest font-bold text-amber-300 flex items-center gap-1.5">
              <Lightbulb className="h-3.5 w-3.5" /> Suggested next guides
            </p>
            <p className="text-sm text-muted-foreground">
              Ranked by total venue + event signal. Click <em>Generate</em> to queue a draft.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="suggestions-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No high-signal gaps left in the catalog. Add more venues to surface new ideas.
          </p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {rows.map((s) => {
              const key = `${s.city}-${s.tag}`;
              return (
                <div
                  key={key}
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background/40 hover:border-fuchsia-400/40 transition-colors"
                  data-testid={`suggestion-${key}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{s.suggested_title}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {titleize(s.city)} · tag: <code className="text-amber-300">{s.tag}</code> · signal: <strong>{s.signal}</strong>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => generate(s)}
                    disabled={creating === key}
                    className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90"
                    data-testid={`suggestion-generate-${key}`}
                  >
                    {creating === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1.5" /> Generate</>}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ===========================================================================
// CitySeedModal — AI-generate ~N venues per archetype for any city
// ===========================================================================
const ARCHETYPES = [
  { id: 'cocktail_bars', label: 'Cocktail bars' },
  { id: 'restaurants',   label: 'Restaurants' },
  { id: 'wine_bars',     label: 'Wine bars' },
  { id: 'breweries',     label: 'Breweries' },
  { id: 'rooftops',      label: 'Rooftops' },
  { id: 'music_venues',  label: 'Music venues' },
  { id: 'comedy_clubs',  label: 'Comedy clubs' },
  { id: 'coffee_shops',  label: 'Coffee shops' },
  { id: 'dive_bars',     label: 'Dive bars' },
  { id: 'wineries',      label: 'Wineries' },
  { id: 'brunch_spots',  label: 'Brunch spots' },
  { id: 'speakeasies',   label: 'Speakeasies' },
];

function CitySeedModal({ onClose, onDone }) {
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [selected, setSelected] = useState(new Set(['cocktail_bars', 'restaurants', 'wine_bars']));
  const [perArchetype, setPerArchetype] = useState(8);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!city.trim()) {
      toast.error('Enter a city name.');
      return;
    }
    if (selected.size === 0) {
      toast.error('Pick at least one archetype.');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/seed-city-ai`,
        {
          city: city.trim(),
          state: state.trim() || undefined,
          archetypes: Array.from(selected),
          per_archetype: Number(perArchetype) || 8,
        },
        { headers: authHeaders(), timeout: 180000 },
      );
      setResult(r.data);
      toast.success(`Seeded ${r.data?.inserted || 0} new venues in ${r.data?.city}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Seed failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-4 pt-20 overflow-y-auto"
      data-testid="city-seed-modal"
    >
      <Card className="w-full max-w-2xl border-border">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-xl font-bold font-['Space_Grotesk']">Seed a city with AI</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Claude generates ~N plausible venues per archetype. Each is tagged
                <code className="px-1 py-0.5 mx-1 rounded bg-muted text-fuchsia-300">ai_generated</code>
                so you can audit + replace them later.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              data-testid="city-seed-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {!result ? (
            <form onSubmit={submit} className="space-y-5">
              <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
                <div>
                  <Label className="text-xs">City</Label>
                  <Input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Austin"
                    data-testid="city-seed-city"
                  />
                </div>
                <div>
                  <Label className="text-xs">State (optional)</Label>
                  <Input
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="TX"
                    data-testid="city-seed-state"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Archetypes ({selected.size} selected)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {ARCHETYPES.map((a) => {
                    const on = selected.has(a.id);
                    return (
                      <button
                        type="button"
                        key={a.id}
                        onClick={() => toggle(a.id)}
                        data-testid={`city-seed-archetype-${a.id}`}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors text-left ${
                          on
                            ? 'border-fuchsia-400 bg-fuchsia-500/15 text-foreground'
                            : 'border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 items-end">
                <div>
                  <Label className="text-xs">Per archetype</Label>
                  <Input
                    type="number"
                    min={3}
                    max={15}
                    value={perArchetype}
                    onChange={(e) => setPerArchetype(e.target.value)}
                    data-testid="city-seed-per"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {selected.size * (Number(perArchetype) || 8)} venues will be requested total.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 text-white border-0 hover:opacity-90 h-10"
                  data-testid="city-seed-submit"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating with Claude…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate venues</>}
                </Button>
              </div>
              {submitting && (
                <p className="text-[11px] text-muted-foreground">
                  This takes 30-90 seconds depending on how many archetypes you picked.
                </p>
              )}
            </form>
          ) : (
            <div className="space-y-4" data-testid="city-seed-result">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
                <p className="font-bold font-['Space_Grotesk'] text-lg">
                  Seeded {result.city} · +{result.inserted} new · {result.updated} updated
                </p>
                <p className="text-xs text-muted-foreground">
                  {result.guides_cleared || 0} guide(s) freshness flag cleared.
                </p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">By archetype</p>
                <div className="space-y-1.5">
                  {(result.archetypes || []).map((a) => (
                    <div key={a.archetype} className="flex items-center justify-between text-sm">
                      <span>{ARCHETYPES.find((x) => x.id === a.archetype)?.label || a.archetype}</span>
                      <span className="text-muted-foreground">
                        +{a.inserted} new · {a.updated} updated · {a.generated} generated
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {result.samples?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Sample</p>
                  <div className="space-y-1">
                    {result.samples.map((s, i) => (
                      <p key={i} className="text-sm">
                        <span className="font-semibold">{s.name}</span>
                        <span className="text-muted-foreground"> · {s.neighborhood || '—'} · {s.archetype}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
                <Button
                  variant="outline"
                  disabled={result.verifying}
                  onClick={async () => {
                    setResult((r) => ({ ...r, verifying: true }));
                    try {
                      const rr = await axios.post(
                        `${API}/admin/venues/bulk-verify-places`,
                        { city: result.city, only_ai_generated: true, limit: result.inserted || 30 },
                        { headers: authHeaders(), timeout: 180000 },
                      );
                      const d = rr.data || {};
                      if (d.quota_blocked) {
                        toast.error('Google Places quota hit — try again tomorrow.');
                      } else {
                        toast.success(`Verified ${d.verified || 0} of ${d.checked || 0} via Google Places`);
                      }
                      setResult((r) => ({ ...r, verifying: false, verify_summary: d }));
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'Verify failed');
                      setResult((r) => ({ ...r, verifying: false }));
                    }
                  }}
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid="city-seed-verify-all"
                >
                  {result.verifying
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying with Places…</>
                    : <><ShieldCheck className="h-4 w-4 mr-2" /> Verify all with Places</>}
                </Button>
                <Button variant="outline" onClick={() => { setResult(null); setCity(''); setState(''); }} data-testid="city-seed-again">
                  Seed another city
                </Button>
                <Button onClick={onDone} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white" data-testid="city-seed-done">
                  Done
                </Button>
              </div>
              {result.verify_summary && (
                <div className="rounded-lg border border-border bg-card/40 p-3 text-xs space-y-1" data-testid="city-seed-verify-summary">
                  <p className="font-semibold">Places verification</p>
                  <p className="text-muted-foreground">
                    {result.verify_summary.verified || 0} verified ·
                    {' '}{result.verify_summary.no_match || 0} no-match ·
                    {' '}{result.verify_summary.errors || 0} errors
                    {result.verify_summary.quota_blocked ? ' · daily quota exceeded' : ''}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// PhotoUploadButton — small "Upload" pill that pushes files to object storage
// and hands back CDN URLs (used by the card Photos field).
// ===========================================================================
function PhotoUploadButton({ onUploaded, remaining = 8, testId }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(1, remaining));
    e.target.value = '';
    if (!files.length) return;
    if (remaining <= 0) {
      toast.error('Photo limit reached (max 8 per card). Remove one first.');
      return;
    }
    setUploading(true);
    const urls = [];
    for (const f of files) {
      if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) {
        toast.error(`${f.name}: use JPG, PNG, WebP, or GIF.`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 10 MB).`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await axios.post(
          `${API}/editorial-guides/upload-cover`,
          fd,
          { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
        );
        if (r.data?.url) urls.push(r.data.url);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (urls.length) {
      onUploaded(urls);
      toast.success(`${urls.length} photo${urls.length === 1 ? '' : 's'} uploaded`);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-2.5 py-1 text-[10px] font-semibold text-fuchsia-200 hover:bg-fuchsia-500/20 transition-colors disabled:opacity-60"
        data-testid={testId || 'photo-upload-btn'}
      >
        {uploading
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <ImageIcon className="h-3 w-3" />}
        {uploading ? 'Uploading…' : 'Upload photos'}
      </button>
      <input
        type="file"
        ref={inputRef}
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={onPick}
      />
    </>
  );
}


// ===========================================================================
// CoverImageUploader — drag-and-drop / click-to-upload with object-storage backend
// ===========================================================================
function CoverImageUploader({ value, onChange }) {
  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [showUrl, setShowUrl] = React.useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error('Use JPG, PNG, WebP, or GIF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large (max 10 MB).');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(
        `${API}/editorial-guides/upload-cover`,
        fd,
        {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        },
      );
      onChange(r.data?.url || '');
      toast.success('Cover uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  return (
    <div data-testid="cover-uploader">
      {value ? (
        <div className="relative group rounded-xl overflow-hidden border border-border">
          <img src={value} alt="cover" className="w-full h-32 object-cover" />
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => inputRef.current?.click()}
              className="bg-background/90"
              data-testid="cover-replace"
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ImageIcon className="h-3.5 w-3.5 mr-1.5" />}
              Replace
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange('')}
              className="bg-background/90 text-red-300 border-red-500/40 hover:bg-red-500/10"
              data-testid="cover-clear"
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
            dragging
              ? 'border-fuchsia-400 bg-fuchsia-500/5'
              : 'border-border hover:border-fuchsia-400/60'
          }`}
          data-testid="cover-dropzone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-fuchsia-300" /> Uploading…
            </div>
          ) : (
            <>
              <ImageIcon className="h-6 w-6 mx-auto mb-2 text-fuchsia-300" />
              <p className="text-sm font-semibold">Drop an image or click to upload</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                JPG / PNG / WebP / GIF · up to 10 MB
              </p>
            </>
          )}
        </div>
      )}
      <input
        type="file"
        ref={inputRef}
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={onPick}
        data-testid="cover-input"
      />
      <button
        type="button"
        onClick={() => setShowUrl((s) => !s)}
        className="text-[11px] text-muted-foreground hover:text-foreground mt-2 underline"
        data-testid="cover-url-toggle"
      >
        {showUrl ? 'Hide' : 'Or paste an image URL'}
      </button>
      {showUrl && (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="mt-2"
          data-testid="cover-url-input"
        />
      )}
    </div>
  );
}


// ===========================================================================
// ErrorBoundary — keeps a crashing modal from blanking the whole /admin page.
// ===========================================================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[edit-guide-boundary]', error, info);
    try { toast.error('The editor crashed — your changes were NOT saved. Re-open to retry.'); } catch (_e) { /* toast may not be mounted */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div
          className="fixed inset-0 z-50 bg-background/90 flex items-center justify-center p-4"
          data-testid="edit-guide-crash"
        >
          <Card className="max-w-md border-red-500/40">
            <CardContent className="p-6 space-y-3 text-center">
              <AlertTriangle className="h-8 w-8 mx-auto text-red-300" />
              <p className="font-bold font-['Space_Grotesk'] text-lg">The editor hit an error</p>
              <p className="text-xs text-muted-foreground">
                Your changes were not saved. Close this and re-open the editor to retry.
              </p>
              <Button onClick={this.props.onClose} className="bg-fuchsia-500 text-white hover:bg-fuchsia-400">
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

